// ============================================================================
// batch-queue.js — Phase 2 Foundation orchestrator (BQ-CP-3)
// ----------------------------------------------------------------------------
// Headless queue runner for batch quote processing. Pure JavaScript module —
// NO React, NO DOM, NO closure-coupled state from any caller. Pipeline
// functions are passed in as a `PipelineRunner` injection contract so the
// orchestrator works identically with App.jsx-resident wrappers (current
// state) or the future extracted `quote-generation.js` service (post Sub-CP-a).
//
// Architecture: per Council DEC-BQ-015 (orchestrator design) +
// PHASE-2-QUEUE-DESIGN.md §3 (2-layer separation) + DEC-BQ-007 (sequential
// per tab — single `currentProjectId` + `projectAbortRef` mean we MUST
// process one project at a time).
//
// 2026-05-06 (DEC-TQ-001): phase-aware queue. Each row carries
// `phase: 'pre-processing' | 'prepare-quote'`. enqueue() defaults to
// 'prepare-quote' so legacy call sites keep their behaviour. startQueue()
// filters next-pending by phase + drives the runner whose `stages` array
// matches that phase. Pre-processing runner has stages=['preprocess'];
// prepare-quote runner keeps stages=[1..5]. CustomEvents include `phase`
// in their detail; pre-existing listeners that ignore the field are
// unaffected.
//
// State machine: per PHASE-2-QUEUE-DESIGN.md §4.3.
//
//                 ┌─────────────┐
//                 │   pending   │ ← enqueue()
//                 └──────┬──────┘
//                        │ start (queue active + my turn)
//                        ▼
//                 ┌─────────────┐
//                 │ processing  │
//                 └──────┬──────┘
//                        │
//        ┌───────────────┼───────────────┬──────────────┐
//        │               │               │              │
//        ▼               ▼               ▼              ▼
//    completed        failed         skipped       cancelled
//                   (retry-able)   (skipCurrent)  (abortQueue)
//                        │
//                        │ retryFailed()
//                        ▼
//                    pending
//
// CustomEvents emitted on `window` (per PHASE-2-QUEUE-DESIGN.md §5):
//   qm-batch-queue-enqueued, qm-batch-queue-started, qm-batch-job-started,
//   qm-batch-stage-advance, qm-batch-job-completed, qm-batch-job-failed,
//   qm-batch-queue-paused, qm-batch-queue-resumed, qm-batch-queue-drained,
//   qm-batch-queue-idle (the whole run is over: getQueueStatus().active is
//   false; a prepare-all run's drains come while it is still active)
//
// Persistence: every state transition writes to Dexie `batch_jobs` (v23)
// BEFORE emitting the CustomEvent. Tab-close mid-transition keeps state
// coherent.
// ============================================================================

import db from './db';
import { classifyAiError } from './ai-error-handling';
import { classifyAiServiceFailure } from './ai-service-failure';
import { evaluateProjectIntakeReadiness, hasDurableIntakeReadyProof } from './project-intake-readiness.js';
import { projectHasGeneratedWork } from './project-intake-persistence.js';
import { isProjectDeletionTombstone } from './project-deletion-tombstone.js';
import { isBrainPipelineVersion, isBrainProject } from '../utils/quote-brain-project';

// ─── Module-scope active-queue state ───────────────────────────────────────
// One queue per browser tab (DEC-BQ-007). These mirror the Dexie state for
// the currently-running batch but live in memory for low-latency UI reads.
let _isActive = false;
let _isPaused = false;
let _abortRequested = false;
// Quote Run "Stop after this job": the job in progress finishes, then the run
// ends and the jobs it had not started are cancelled as not run. Read at the
// top of the processing loop, after that job's terminal write.
let _stopAfterCurrent = false;
// The same stop armed by a run-wide fault (requestStopAfterCurrentJob
// keepNotStarted): the jobs not started stay pending, on the run list, for
// the next run, because every one of them would have stopped the same way.
let _stopKeepsNotStarted = false;
// Set once that stop has ended the run: no sweep cancels the jobs it kept,
// and the drain says how many there are (keptNotStarted, notStarted).
let _runEndedKeepingNotStarted = false;
let _activeBatchId = null;
let _currentJobId = null;
let _activeCohortJobIds = new Set();
let _activeRunCompletion = Promise.resolve();
let _resolveActiveRunCompletion = null;
let _isAllQueueActive = false;
let _activeAllRunId = null;
let _activeAllCohortJobIds = new Set();
// Quote Run: jobs put back on the run list while a run goes (queueStaged).
// They keep their id, so without this they would still be among the jobs the
// run froze: claimed by it, and cancelled by its Stop. They wait for the next
// run instead. Cleared when a run starts.
let _leftRunJobIds = new Set();
let _activeAllRunCompletion = Promise.resolve();
let _resolveActiveAllRunCompletion = null;
const JOB_LEASE_DURATION_MS = 90_000;
const JOB_LEASE_HEARTBEAT_MS = 20_000;
// The Quote Run lease. The heartbeat renews every JOB_LEASE_HEARTBEAT_MS, but a
// browser throttles timers in a background tab to roughly one a minute, and a
// tick is skipped outright while the previous one is still in flight. At 90s
// two throttled ticks could miss the window.
//
// Live, 2026-09-20: Waverton waited out two provider rate limits, resumed, and
// was then failed at Stage 2 by PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE: its own
// lease had lapsed while nobody else wanted it. 180 is the server's maximum
// (BROWSER_QUOTE_RUN_CLAIM_TTL_MAX_SECONDS), giving three throttled ticks of
// margin. It only governs how long an abandoned run blocks another session.
const QUOTE_RUN_CLAIM_TTL_SECONDS = 180;

// How long a claimed job may sit on the SAME stage before the queue says so.
//
// This is a REPORTING threshold, not a timeout. Nothing is cancelled when it
// trips — the standing rule is "no timeouts on long AI pipelines" (a WMS or
// costing call legitimately runs for many minutes, and aborting one produces a
// phantom "Cancelled by user"). What this fixes is the SILENCE: before it, the
// lease heartbeat renewed every 20s whether or not any progress was being made,
// so a job whose stage handler never settled held its lease forever, rendered as
// a normal in-progress row with a growing elapsed counter, and no other tab
// would ever recover it.
//
// 25 minutes sits above the slowest legitimate single stage observed on real
// Customer Zero work (Stage 1 analysis on a photo-heavy job) with headroom, so a
// healthy job never trips it.
const JOB_STALL_WARN_MS = 25 * 60_000;

/**
 * Should this heartbeat report a stall?
 *
 * Extracted as a pure function on purpose. The wiring around it lives inside a
 * setInterval created deep in startQueue's async body, which a fake-timer
 * harness could not be made to drive reliably — so the DECISION is unit-tested
 * directly here rather than being inferred from a timer that may or may not
 * have fired. See batch-queue-stall-watchdog.test.js.
 *
 * @param {{lastProgressMs:number, now:number, alreadyReported:boolean, leaseLost:boolean, thresholdMs?:number}} state
 */
function _shouldReportStall({
    lastProgressMs,
    now,
    alreadyReported,
    leaseLost,
    thresholdMs = JOB_STALL_WARN_MS,
} = {}) {
    if (leaseLost) return false;          // a lost lease is a different failure
    if (alreadyReported) return false;    // one report per stall, not per heartbeat
    if (!Number.isFinite(lastProgressMs) || !Number.isFinite(now)) return false;
    return (now - lastProgressMs) >= thresholdMs;
}
let _runnerOwnerId = null;
// Serialises batch_jobs compare-and-mutate boundaries in this tab. Production
// also wraps them in one IndexedDB read/write transaction, which serialises the
// same boundary across tabs. Without both pieces, two near-simultaneous Menu
// actions can act on stale status snapshots (duplicate enqueue, demote a newly
// claimed job, or delete a newly queued row).
let _enqueueSerial = Promise.resolve();

function _withEnqueueWriteLock(operation, targetDb = db) {
    const run = _enqueueSerial.then(() => {
        if (typeof targetDb?.transaction === 'function') {
            return targetDb.transaction('rw', targetDb.batch_jobs, operation);
        }
        return operation();
    });
    _enqueueSerial = run.then(() => undefined, () => undefined);
    return run;
}

function _withTransaction(targetDb, tables, operation) {
    if (typeof targetDb?.transaction === 'function') {
        return targetDb.transaction('rw', ...tables, operation);
    }
    return operation();
}

// ─── ID + time helpers ─────────────────────────────────────────────────────
function _newBatchId() {
    // Crypto-strong UUID when available; fallback for tests.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `batch_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function _getRunnerOwnerId() {
    if (!_runnerOwnerId) _runnerOwnerId = `queue-runner:${_newBatchId()}`;
    return _runnerOwnerId;
}

/**
 * This page's runner id, the `claimOwnerId` every job it claims carries.
 * Read-only: quote-run-controls.ts writes it into the run marker so a
 * reloaded page can tell the jobs its own run claimed from another tab's.
 */
export function getRunnerOwnerId() {
    return _getRunnerOwnerId();
}

function _nowIso() {
    return new Date().toISOString();
}

function _backendUpdatedAtFromReconciliation(result, projectId) {
    const value = result?.backendUpdatedAt
        ?? result?.backendProject?.updatedAt
        ?? result?.backendProject?.updated_at
        ?? result?.project?.backendVerifiedUpdatedAt
        ?? result?.project?.backend_verified_updated_at
        ?? null;
    if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) {
        const error = new Error(`Queued Project ${projectId} has no exact backend updatedAt receipt.`);
        error.code = 'PROJECT_QUOTE_RUN_VERSION_MISSING';
        error.category = 'project-coordination';
        error.retryable = true;
        throw error;
    }
    return value;
}

/**
 * The Project version this tab's own saves last produced, or null.
 *
 * Every Stage 1 content save moves `projects.updated_at`, so a claim renewal
 * pinned to the version from the last reconciliation is refused with
 * PROJECT_STALE_WRITE while the run is working. The fallback reconciliation
 * compares the local row with the backend and refuses while the tab keeps
 * saving, so the lease lapsed and the run stopped for review (Quote Run,
 * 2026-09-28). The tab's save token only ever advances through its own
 * writes, which the server accepted against the previous token, so renewing
 * with it cannot hide another device's edit: that edit leaves the token
 * behind and the server still refuses.
 */
function _ownProjectVersion(pipelineRunner, projectId) {
    if (typeof pipelineRunner?.getOwnProjectVersion !== 'function') return null;
    try {
        const value = pipelineRunner.getOwnProjectVersion(projectId);
        return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;
    } catch {
        return null;
    }
}

function _isStaleProjectWrite(error) {
    return String(error?.code || error?.errorCode || '') === 'PROJECT_STALE_WRITE';
}

/**
 * Our own lease ran out, and the server reports nobody holds one. Distinct from
 * OWNER_MISMATCH / BUSY, which mean another session took over: those must still
 * stop this run.
 */
const _MAX_LAPSED_CLAIM_REACQUIRES = 3;

function _isLapsedOwnQuoteRunClaim(error) {
    return new Set([
        'PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE',
        'PROJECT_QUOTE_RUN_CLAIM_NOT_FOUND',
    ]).has(String(error?.code || error?.errorCode || ''));
}

function _isDefinitiveQuoteRunClaimLoss(error) {
    return new Set([
        'PROJECT_QUOTE_RUN_OWNER_MISMATCH',
        'PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE',
        'PROJECT_QUOTE_RUN_CLAIM_NOT_FOUND',
        'PROJECT_QUOTE_RUN_RECEIPT_INVALID',
        'PROJECT_NOT_FOUND',
    ]).has(String(error?.code || error?.errorCode || ''));
}

function _quoteRunClaimUncertain(error, projectId) {
    const uncertainty = new Error(
        'QuoteMaker could not confirm that this session still owns the Project run. It paused before the next stage.',
    );
    uncertainty.code = 'PROJECT_QUOTE_RUN_CLAIM_UNCERTAIN';
    uncertainty.category = 'project-coordination';
    uncertainty.retryable = true;
    uncertainty.status = Number(error?.status || error?.statusCode || 0) || undefined;
    uncertainty.cause = error;
    uncertainty.details = {
        projectId,
        reason: 'claim-renewal-unconfirmed',
        causeCode: String(error?.code || error?.errorCode || '') || null,
    };
    return uncertainty;
}

function _quoteRunReleaseWarning(error, outcome) {
    return {
        at: _nowIso(),
        outcome,
        code: String(error?.code || error?.errorCode || 'PROJECT_QUOTE_RUN_RELEASE_FAILED'),
        message: String(error?.message || 'Quote Run claim release could not be confirmed.'),
    };
}

function _cleanDisplayValue(value, maxLength) {
    return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

function _firstNonBlank(...values) {
    for (const value of values) {
        const cleaned = _cleanDisplayValue(value, Number.MAX_SAFE_INTEGER);
        if (cleaned) return cleaned;
    }
    return '';
}

function _isHumanDisplayName(value) {
    const name = _cleanDisplayValue(value, 500);
    const uuidShape = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return !!name
        && !uuidShape.test(name)
        && !['queued project', 'untitled project', 'project details loading…']
            .includes(name.toLowerCase());
}

function _normaliseDisplayMeta(meta, projectId) {
    if (!meta || typeof meta !== 'object') return null;
    const candidateName = _firstNonBlank(meta.name, meta.title);
    const name = _isHumanDisplayName(candidateName) ? candidateName.slice(0, 500) : '';
    return {
        name,
        address: _firstNonBlank(meta.address, meta.siteAddress).slice(0, 1000),
        quoteRef: _firstNonBlank(meta.quoteRef, meta.quoteNumber).slice(0, 200),
        relativePath: _firstNonBlank(meta.relativePath, meta.path).slice(0, 2000),
        projectId,
    };
}

function _normaliseSelectedFileIds(value, operation = 'enqueue') {
    if (value === undefined || value === null) return null;
    if (!Array.isArray(value)) {
        throw new Error(`${operation}: selectedFileIds must be an array of file id strings, null, or undefined`);
    }
    const cleaned = Array.from(new Set(
        value
            .filter(item => typeof item === 'string' && item.trim().length > 0)
            .map(item => item.trim()),
    ));
    return cleaned.length > 0 ? cleaned : null;
}

function _normaliseRestartLineage(value, projectId, operation = 'enqueue') {
    if (value === undefined || value === null) return null;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`${operation}: restartLineage must be an object, null, or undefined`);
    }
    const restartOfProjectId = String(
        value.restartOfProjectId ?? value.restart_of_project_id ?? '',
    ).trim();
    const uuidShape = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidShape.test(restartOfProjectId)
        || restartOfProjectId.toLowerCase() === String(projectId || '').toLowerCase()) {
        const error = new Error(`${operation}: restartLineage requires a different predecessor project UUID`);
        error.code = 'PROJECT_RESTART_LINEAGE_INVALID';
        error.retryable = false;
        throw error;
    }
    const intakeAttemptKind = String(
        value.intakeAttemptKind ?? value.intake_attempt_kind ?? 'restart-from-source',
    ).trim() || 'restart-from-source';
    const intakeRestartedAt = String(
        value.intakeRestartedAt ?? value.intake_restarted_at ?? '',
    ).trim() || null;
    return { restartOfProjectId, intakeAttemptKind, intakeRestartedAt };
}

function _restartLineageFromJob(job, projectId = job?.projectId) {
    const restartOfProjectId = job?.restartOfProjectId ?? job?.restart_of_project_id;
    if (!restartOfProjectId) return null;
    return _normaliseRestartLineage({
        restartOfProjectId,
        intakeAttemptKind: job?.intakeAttemptKind ?? job?.intake_attempt_kind,
        intakeRestartedAt: job?.intakeRestartedAt ?? job?.intake_restarted_at,
    }, projectId, 'queue audit');
}

function _mergeDisplayMetaNonBlank(existingMeta, incomingMeta, projectId) {
    const existing = _normaliseDisplayMeta(existingMeta, projectId) || {};
    const incoming = _normaliseDisplayMeta(incomingMeta, projectId) || {};
    return {
        name: _firstNonBlank(incoming.name, existing.name).slice(0, 500),
        address: _firstNonBlank(incoming.address, existing.address).slice(0, 1000),
        quoteRef: _firstNonBlank(incoming.quoteRef, existing.quoteRef).slice(0, 200),
        relativePath: _firstNonBlank(incoming.relativePath, existing.relativePath).slice(0, 2000),
        projectId,
    };
}

function _normaliseBatchError(err, { phase = null, stage = null } = {}) {
    const statusCode = Number(err?.statusCode || err?.status || err?.body?.status || 0) || null;
    const candidateMessages = [
        err?.message,
        typeof err?.error === 'string' ? err.error : (typeof err?.error?.message === 'string' ? err.error.message : null),
        typeof err?.detail === 'string' ? err.detail : (typeof err?.detail?.message === 'string' ? err.detail.message : null),
        typeof err?.body?.message === 'string' ? err.body.message : null,
        typeof err?.body?.error === 'string' ? err.body.error : (typeof err?.body?.error?.message === 'string' ? err.body.error.message : null),
        typeof err?.cause?.message === 'string' ? err.cause.message : null,
        typeof err?.details?.reason === 'string' ? err.details.reason : null,
        typeof err?.statusText === 'string' ? err.statusText : null,
        typeof err === 'string' ? err : null,
    ];
    let rawMessage = candidateMessages.find(m => typeof m === 'string' && m.trim() && m.trim() !== '[object Object]');
    if (!rawMessage && statusCode) {
        rawMessage = `Server returned HTTP ${statusCode}${statusCode === 503 ? ' (Service Unavailable)' : statusCode === 502 ? ' (Bad Gateway)' : statusCode === 504 ? ' (Gateway Timeout)' : statusCode === 404 ? ' (Not Found)' : ''}`;
    }
    const message = (rawMessage && String(rawMessage).trim() && String(rawMessage) !== '[object Object]')
        ? String(rawMessage).trim()
        : (err?.name && err.name !== 'Error' ? err.name : 'unknown error');
    let code = String(err?.code || err?.errorCode || err?.body?.code || '').trim();
    // A real fetch abort is a DOMException, whose legacy numeric `.code` is 20.
    // A bare number is never one of this app's codes; without this the job row
    // and the failed event carried the code "20".
    if (/^\d+$/.test(code)) code = '';
    // A document the file service refused (data-service-api.js marks it
    // final) that reaches the queue from a save outside the reading gets the
    // same words and the same next step as one refused while the files were
    // read (project-intake-persistence.js backendSyncError).
    if (code === 'DOCUMENT_DURABILITY_REQUIRED' && err?.retryable === false) code = 'DOCUMENT_NOT_STORED';
    if (!code) {
        // A stage interrupted by something other than the operator's Stop (a
        // fetch bound to a project signal that a reload replaced). Retryable:
        // the work is idempotent and nothing about the project is wrong.
        if (_isAbortError(err)) code = 'PROJECT_RUN_INTERRUPTED';
        else if (/not found in Dexie|not available locally/i.test(message)) code = 'PROJECT_NOT_LOCAL';
        else if (/AI provider key is not configured|OPENROUTER_API_KEYS?.*not configured/i.test(message)) code = 'ai_provider_not_configured';
        else if (/no matching endpoints|data polic/i.test(message)) code = 'AI_NO_MATCHING_ENDPOINT';
        else if (statusCode === 401 || statusCode === 403 || /401|unauthori|invalid api key|authentication/i.test(message)) code = 'AI_AUTH_FAILED';
        else if (statusCode === 402 || /402|spend cap|out of credit/i.test(message)) code = 'AI_CREDIT_EXHAUSTED';
        else if (statusCode === 429 || /429|rate.?limit|too many requests/i.test(message)) code = 'AI_RATE_LIMITED';
        else if (statusCode === 504 || /timeout|timed out/i.test(message)) code = 'STAGE_TIMEOUT';
        else if (statusCode === 502 || statusCode === 503 || /network|failed to fetch|offline|service unavailable|bad gateway/i.test(message)) code = 'NETWORK_FAILED';
        else if (statusCode === 409 || /conflict|stale/i.test(message)) code = 'PROJECT_CONFLICT';
        else code = 'PIPELINE_STAGE_FAILED';
    }
    const explicitlyRetryable = typeof err?.retryable === 'boolean' ? err.retryable : (typeof err?.body?.retryable === 'boolean' ? err.body.retryable : null);
    const nonRetryableCodes = new Set([
        'project_not_local',
        'ai_no_matching_endpoint',
        'ai_auth_failed',
        'ai_credit_exhausted',
        'ai_provider_not_configured',
    ]);
    const retryable = explicitlyRetryable ?? !nonRetryableCodes.has(code.toLowerCase());
    return {
        message,
        code,
        retryable,
        details: {
            phase,
            stage,
            statusCode,
            provider: err?.provider || null,
            model: err?.model || null,
            category: err?.category || null,
            operatorAction: err?.operatorAction || err?.body?.operatorAction || null,
            reason: err?.details?.reason || null,
            ...(err?.details?.causeCode
                ? { causeCode: String(err.details.causeCode) }
                : {}),
            // The file a refused document stop names (DOCUMENT_NOT_STORED).
            ...(err?.details?.documentName
                ? { documentName: String(err.details.documentName).slice(0, 200) }
                : {}),
            ...(Number.isFinite(Number(err?.details?.causeStatus)) && Number(err?.details?.causeStatus) > 0
                ? { causeStatus: Number(err.details.causeStatus) }
                : {}),
            reviewStage: err?.details?.reviewStage ?? null,
            evidence: Array.isArray(err?.details?.evidence) ? err.details.evidence : null,
            typeBreakdown: err?.details?.typeBreakdown || null,
            counts: err?.details?.counts || err?.details?.readiness?.counts || null,
            issues: err?.details?.issues || err?.details?.readiness?.issues || null,
            warnings: err?.details?.warnings || err?.details?.readiness?.warnings || null,
            pricing: err?.details?.pricing || err?.details?.readiness?.pricing || null,
            coverage: err?.details?.coverage || err?.details?.readiness?.coverage || null,
            diagnostics: _boundedErrorDiagnostics(err?.details),
            // A busy refusal says when the claim that refused it ends (the
            // 409's lease_expires_at, projects.js quote-run-claim). The Quote
            // Run reads it to tell this browser's own closed page apart from
            // another session (import-workflow closedPageClaimHold).
            ...(code === 'PROJECT_QUOTE_RUN_BUSY' && Number.isFinite(Date.parse(String(err?.body?.lease_expires_at || '')))
                ? { claimFreesAt: new Date(Date.parse(String(err.body.lease_expires_at))).toISOString() }
                : {}),
        },
    };
}

// Five waits, about fifteen minutes in all (60s, 120s, 180s, 240s, 300s on a
// 60s hint). Three was about five, and a real OpenRouter rate-limit episode on
// the cloud (2026-09-20) outlasted it: Hornsby spent two waits on a 429 and a
// key quarantine and had one left when the provider faulted a third time. Only
// transient kinds are ever waited on; authentication, credit and configuration
// failures still stop at once, so the longer bound cannot hide one of those.
const _MAX_PROVIDER_RETRIES = 5;
const _PROVIDER_WAIT_MIN_MS = 15 * 1000;
const _PROVIDER_WAIT_MAX_MS = 5 * 60 * 1000;
const _PROVIDER_WAIT_DEFAULT_MS = 60 * 1000;
const _SOFT_QUARANTINE_MAX_HINT_SECONDS = 90;

const _PROVIDER_RETRY_CARRY_MS = 10 * 60 * 1000;

function _providerRetriesThisRun(currentJob, batchId) {
    const count = Number(currentJob?.providerRetryCount) || 0;
    if (currentJob?.providerRetryBatchId === batchId) return count;
    // A different run waited on this SAME row moments ago: a second tab whose
    // cohort shares the job. Scoping the count to the batch alone let two tabs
    // reset each other's counter forever, so the bound of three was never
    // reached while the provider stayed down. An operator's "Try again" clears
    // these fields (retryFailed), and an old wait has simply aged out.
    const lastWaitMs = Date.parse(String(currentJob?.providerWaitUntil || ''));
    return Number.isFinite(lastWaitMs) && Math.abs(Date.now() - lastWaitMs) < _PROVIDER_RETRY_CARRY_MS
        ? count
        : 0;
}

/**
 * How long to wait before retrying a job that failed only because the AI
 * service is temporarily paused. 0 means "this is not that": fail normally.
 *
 * Deliberately narrow. Authentication, credit, configuration and data-policy
 * failures need the operator and are never waited on. The wait honours the
 * gateway's own retryAfterSeconds hint (its key quarantine is 60s for a rate
 * limit, 15s for a wobble), padded slightly and lengthened on each attempt.
 * This is a pause between attempts, not a timeout on any AI request.
 */
function _transientProviderWaitMs(err, failure, currentJob, batchId) {
    const attempts = _providerRetriesThisRun(currentJob, batchId);
    if (attempts >= _MAX_PROVIDER_RETRIES) return 0;
    const classification = classifyAiError(err);
    const code = String(failure?.code || '');
    // One shared judgment with the evidence lanes (ai-service-failure.ts), so a
    // gateway timeout, a cut stream or a dropped connection is waited on here
    // too. Live, 2026-09-20: Westmead's first failure was upstream_timeout and
    // it failed the project outright.
    const transient = classifyAiServiceFailure(err).transient
        || code === 'AI_RATE_LIMITED'
        || code === 'NETWORK_FAILED';
    if (!transient) return 0;
    const hintedSeconds = Number(err?.retryAfterSeconds) || Number(classification.retryAfterSeconds) || 0;
    // The gateway's hint is when its SOONEST key recovers: 60s for a rate limit,
    // 15s for a wobble, five minutes for authentication or credit. A hint well
    // past 60s therefore means EVERY key is in the five-minute cooldown, which
    // only an auth or credit failure produces. That needs the operator; waiting
    // cost a seven-project run about half an hour to report it.
    // Only for the quarantine answer: an upstream 429 may carry a long
    // retry-after of its own, and that one is still worth waiting for.
    const allKeysQuarantined = classification.kind === 'ai-provider-keys-quarantined'
        || String(err?.code || '') === 'all_keys_quarantined';
    if (allKeysQuarantined && hintedSeconds > _SOFT_QUARANTINE_MAX_HINT_SECONDS) return 0;
    const hintedMs = hintedSeconds * 1000;
    const baseMs = hintedMs > 0 ? hintedMs + 5000 : _PROVIDER_WAIT_DEFAULT_MS;
    const scaledMs = baseMs * (attempts + 1);
    return Math.min(_PROVIDER_WAIT_MAX_MS, Math.max(_PROVIDER_WAIT_MIN_MS, scaledMs));
}

/**
 * The files a finished Quote Run went without: what intake set aside plus what
 * the run itself set aside because it could not read it (a PDF, a video, a
 * photo). Peter, 2026-10-23: the Quote Run carries on and flags as Full AI
 * does, so the queue row names every such file (DEC-BQ-030: never silent).
 * Read from the finished project with the same readiness rule intake uses;
 * null when the project cannot be read, so the row keeps what it had.
 */
async function _quoteRunSetAsideMedia(job) {
    try {
        const finishedProject = await db.projects.get(job.projectId);
        if (!finishedProject) return null;
        return evaluateProjectIntakeReadiness(finishedProject, { selectedFileIds: job.selectedFileIds || null }).deferred;
    } catch {
        return null;
    }
}

const _MAPPED_ERROR_DETAIL_KEYS = new Set([
    'reason', 'causeCode', 'reviewStage', 'evidence', 'typeBreakdown',
    'counts', 'issues', 'warnings', 'pricing', 'coverage', 'readiness',
]);

/**
 * Carry a throw site's own diagnostics onto the job row.
 *
 * The fixed whitelist above dropped everything specific a gate reports: which
 * lanes differed, the expected and persisted fingerprints, the version tokens.
 * Every stopped job then read as the same sentence with no way to tell why.
 * Only short scalars and short lists of short strings are kept, so a gate can
 * never push project content, file bytes or personal data onto the row.
 */
function _boundedErrorDiagnostics(details) {
    if (!details || typeof details !== 'object' || Array.isArray(details)) return null;
    const out = {};
    let kept = 0;
    for (const key of Object.keys(details)) {
        if (kept >= 16) break;
        if (_MAPPED_ERROR_DETAIL_KEYS.has(key)) continue;
        const value = details[key];
        if (value === null || typeof value === 'number' || typeof value === 'boolean') {
            out[key] = value;
        } else if (typeof value === 'string') {
            out[key] = value.slice(0, 200);
        } else if (Array.isArray(value)
            && value.length <= 20
            && value.every(entry => typeof entry === 'string' || typeof entry === 'number')) {
            out[key] = value.map(entry => (typeof entry === 'string' ? entry.slice(0, 120) : entry));
        } else {
            continue;
        }
        kept += 1;
    }
    return kept > 0 ? out : null;
}

// ─── Event helpers ─────────────────────────────────────────────────────────
// Always wraps the dispatch in try/catch so a missing window (Node tests)
// doesn't break the queue. Events are advisory; persistence is the source
// of truth.
function _emit(eventName, detail) {
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent(eventName, { detail }));
        }
    } catch (err) {
        // Event dispatch is non-load-bearing. Swallow.
    }
}

// ─── Persistence helpers ───────────────────────────────────────────────────
async function _persistUpdate(jobId, patch) {
    if (!db?.batch_jobs) {
        throw new Error('batch_jobs Dexie store unavailable — has db.version(23) shipped?');
    }
    await db.batch_jobs.update(jobId, patch);
    return db.batch_jobs.get(jobId);
}

function _leaseExpiryIso(nowMs = Date.now()) {
    return new Date(nowMs + JOB_LEASE_DURATION_MS).toISOString();
}

function _leaseIsExpired(row, nowMs = Date.now()) {
    const expiresAt = Date.parse(String(row?.leaseExpiresAt || ''));
    return !Number.isFinite(expiresAt) || expiresAt <= nowMs;
}

/**
 * Atomically transition one pending row to processing. IndexedDB read/write
 * transactions are exclusive across connections, so two browser tabs cannot
 * both observe and claim the same job.
 */
export async function _claimNextPendingJob({
    targetDb = db,
    batchId,
    phase,
    ownerId,
    initialStage,
    approvedJobIds = null,
    nowMs = Date.now(),
} = {}) {
    if (!targetDb?.batch_jobs) {
        throw new Error('_claimNextPendingJob requires a batch_jobs store');
    }
    return _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const approved = approvedJobIds instanceof Set
            ? approvedJobIds
            : (Array.isArray(approvedJobIds) ? new Set(approvedJobIds) : null);
        const pending = await targetDb.batch_jobs
            .where('status').equals('pending')
            .and(row => (
                (row.phase || 'prepare-quote') === phase
                && (!approved || approved.has(row.id))
            ))
            .sortBy('position');
        const candidate = pending[0];
        if (!candidate) return null;
        const current = await targetDb.batch_jobs.get(candidate.id);
        if (!current || current.status !== 'pending') return null;
        const claimedAt = new Date(nowMs).toISOString();
        const updated = await targetDb.batch_jobs.update(current.id, {
            status: 'processing',
            batchId,
            startedAt: claimedAt,
            currentStage: initialStage,
            claimOwnerId: ownerId,
            leaseHeartbeatAt: claimedAt,
            leaseExpiresAt: _leaseExpiryIso(nowMs),
        });
        const claimed = await targetDb.batch_jobs.get(current.id);
        if (updated !== 1
            || claimed?.status !== 'processing'
            || claimed?.claimOwnerId !== ownerId) {
            throw new Error(`startQueue: failed to claim pending job ${current.id} for batch ${batchId}`);
        }
        return claimed;
    });
}

async function _renewJobLease(jobId, ownerId, targetDb = db) {
    return _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const row = await targetDb.batch_jobs.get(jobId);
        if (!row || row.status !== 'processing' || row.claimOwnerId !== ownerId) return false;
        const nowMs = Date.now();
        await targetDb.batch_jobs.update(jobId, {
            leaseHeartbeatAt: new Date(nowMs).toISOString(),
            leaseExpiresAt: _leaseExpiryIso(nowMs),
        });
        return true;
    });
}

async function _persistOwnedUpdate(jobId, ownerId, patch, targetDb = db) {
    return _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const row = await targetDb.batch_jobs.get(jobId);
        if (!row || row.claimOwnerId !== ownerId) {
            throw new _LeaseLostError(`Queue lease for job ${jobId} is no longer owned by this tab`);
        }
        await targetDb.batch_jobs.update(jobId, patch);
        return targetDb.batch_jobs.get(jobId);
    });
}

// Terminal writes need one last cancellation check inside the same
// read/write boundary that owns the row. A Stop can arrive after an awaited
// stage/terminal handler resolves but before its completion/failure write;
// checking only in the outer processing loop leaves that race able to stamp a
// stopped job as completed or failed.
async function _persistOwnedUpdateUnlessAborted(
    jobId,
    ownerId,
    patch,
    pipelineRunner,
    targetDb = db,
) {
    return _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const row = await targetDb.batch_jobs.get(jobId);
        if (!row || row.claimOwnerId !== ownerId) {
            throw new _LeaseLostError(`Queue lease for job ${jobId} is no longer owned by this tab`);
        }
        if (_queueAbortRequested(pipelineRunner)) return { aborted: true, row };
        await targetDb.batch_jobs.update(jobId, patch);
        return {
            aborted: false,
            row: await targetDb.batch_jobs.get(jobId),
        };
    });
}

async function _recoverExpiredProcessingLeases(phase, targetDb = db, nowMs = Date.now()) {
    return _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const processing = await targetDb.batch_jobs
            .where('status').equals('processing')
            .and(row => (row.phase || 'prepare-quote') === phase)
            .toArray();
        let recovered = 0;
        for (const row of processing) {
            if (!_leaseIsExpired(row, nowMs)) continue;
            await targetDb.batch_jobs.update(row.id, {
                status: 'pending',
                batchId: null,
                currentStage: null,
                claimOwnerId: null,
                leaseHeartbeatAt: null,
                leaseExpiresAt: null,
                // The only trace a closed or reloaded page leaves: when its run
                // died and the stage it had reached.
                interruptedAt: _nowIso(),
                interruptedAtStage: row.currentStage ?? null,
                // The runner that held it, so this tab can tell its own run's
                // stop from a page that closed.
                interruptedOwnerId: row.claimOwnerId ?? null,
            });
            recovered += 1;
        }
        return recovered;
    });
}

/**
 * Reset every processing row whose lease has expired, in both phases, back to
 * pending, recording where each one stopped (interruptedAt,
 * interruptedAtStage). A row another tab still holds a live lease on is left
 * alone. Unlike hydrate(), this heals nothing else and raises no event, so
 * Quote Run's Carry on can call it just before it starts the run.
 *
 * @returns {Promise<number>} how many rows were reset
 */
export async function recoverExpiredQueueLeases() {
    if (!db?.batch_jobs) return 0;
    const nowMs = Date.now();
    const intake = await _recoverExpiredProcessingLeases('pre-processing', db, nowMs);
    const drafting = await _recoverExpiredProcessingLeases('prepare-quote', db, nowMs);
    return intake + drafting;
}

// DEC-TQ-001: phase-aware existence check. Same projectId can have one
// pre-processing AND one prepare-quote row — they represent different work.
// Pending/processing collision check happens within a phase, not across.
async function _findExistingPending(projectId, phase = 'prepare-quote', targetDb = db) {
    if (!targetDb?.batch_jobs) return null;
    const rows = await targetDb.batch_jobs
        .where('projectId').equals(projectId)
        .and(row => (row.status === 'pending' || row.status === 'processing') && (row.phase || 'prepare-quote') === phase)
        .toArray();
    return rows.length > 0 ? rows[0] : null;
}

// DEC-TQ-001: positions are scoped per-phase. Pre-processing rows occupy
// position 1..N independently of prepare-quote rows so each phase's queue
// is reorderable without touching the other.
async function _nextPendingPosition(phase = 'prepare-quote', targetDb = db) {
    if (!targetDb?.batch_jobs) return 1;
    const rows = await targetDb.batch_jobs
        .where('status').equals('pending')
        .and(r => (r.phase || 'prepare-quote') === phase)
        .toArray();
    if (rows.length === 0) return 1;
    const maxPos = rows.reduce((max, r) => Math.max(max, Number(r.position) || 0), 0);
    return maxPos + 1;
}

/**
 * Read every queue row for a project before enqueueing it.
 *
 * Current Dexie collections expose `where(...).equals(...).toArray()`, but
 * older adapters and a few long-lived offline/test stores only implement the
 * table-level `toArray()` plus filtered collection helpers. A full-table scan
 * is a safe compatibility fallback here because this read is an integrity
 * gate, not an optional optimisation: if neither route can prove the history,
 * enqueue must fail closed so a deleted project UUID cannot be resurrected.
 */
async function _readProjectQueueHistory(projectId, targetDb = db) {
    const table = targetDb?.batch_jobs;
    let indexedReadError = null;

    if (typeof table?.where === 'function') {
        try {
            const collection = table.where('projectId').equals(projectId);
            if (typeof collection?.toArray === 'function') {
                const rows = await collection.toArray();
                if (!Array.isArray(rows)) {
                    throw new TypeError('project queue history index returned a non-array result');
                }
                return rows;
            }
        } catch (error) {
            indexedReadError = error;
        }
    }

    if (typeof table?.toArray === 'function') {
        try {
            const rows = await table.toArray();
            if (!Array.isArray(rows)) {
                throw new TypeError('project queue history table returned a non-array result');
            }
            return rows.filter(row => row?.projectId === projectId);
        } catch (error) {
            const historyError = new Error(
                `enqueue: could not verify queue history for project ${projectId}`,
                { cause: error },
            );
            historyError.code = 'PROJECT_QUEUE_HISTORY_UNAVAILABLE';
            historyError.retryable = false;
            throw historyError;
        }
    }

    const historyError = new Error(
        `enqueue: could not verify queue history for project ${projectId}`,
        indexedReadError ? { cause: indexedReadError } : undefined,
    );
    historyError.code = 'PROJECT_QUEUE_HISTORY_UNAVAILABLE';
    historyError.retryable = false;
    throw historyError;
}

// ─── PUBLIC API: queue management ──────────────────────────────────────────

/**
 * Add a project to the queue at the next pending position. Idempotent — if
 * the projectId is already in the queue (for the given phase) with status
 * 'pending' or 'processing', returns the existing row without creating a
 * duplicate. The same projectId CAN have one pre-processing row AND one
 * prepare-quote row simultaneously (they're separate work).
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote', selectedFileIds?: string[] | null, displayMeta?: object | null, restartLineage?: object | null }} [opts]
 * @returns {Promise<BatchJob>}
 *
 * v5.37.24 (Peter 2026-05-20 — Phase C): `opts.selectedFileIds` is the new
 * per-project scope filter. Pre-processing reads it + only operates on the
 * matching files (or all files if null/empty). The pre-existing
 * idempotent-re-queue branch UPDATES the existing job's scope when the
 * caller supplies a new one — so a user can refine their selection without
 * having to dequeue + re-enqueue. Null/undefined scope means "all files"
 * (backward-compatible with pre-v5.37.24 callers).
 */
function _prepareEnqueue(projectId, opts = {}) {
    if (!projectId || typeof projectId !== 'string') {
        throw new Error('enqueue: projectId is required (string)');
    }
    const phase = opts.phase || 'prepare-quote';
    if (phase !== 'pre-processing' && phase !== 'prepare-quote') {
        throw new Error(`enqueue: phase must be 'pre-processing' or 'prepare-quote', got '${phase}'`);
    }
    // v5.37.24: validate + normalise the scope. Accept array-of-strings,
    // null, or undefined. Anything else is a caller mistake.
    // De-dup + drop empties. An empty array means "no scope" → treat as null.
    const selectedFileIds = _normaliseSelectedFileIds(opts.selectedFileIds, 'enqueue');
    const displayMeta = opts.displayMeta !== undefined
        ? _normaliseDisplayMeta(opts.displayMeta, projectId)
        : undefined;
    const restartLineage = opts.restartLineage !== undefined
        ? _normaliseRestartLineage(opts.restartLineage, projectId)
        : undefined;
    const targetDb = opts.db || db;
    if (!targetDb?.batch_jobs) {
        throw new Error('enqueue: batch_jobs store is unavailable');
    }
    return { projectId, opts, phase, selectedFileIds, displayMeta, restartLineage, targetDb };
}

async function _enqueuePrepared({ projectId, opts, phase, selectedFileIds, displayMeta, restartLineage, targetDb }) {
    const projectHistory = await _readProjectQueueHistory(projectId, targetDb);
    if (projectHistory.some(isProjectDeletionTombstone)) {
        const error = new Error(
            `Project ${projectId} was deleted and cannot be re-queued under the retired UUID. Import its source folder again to create a fresh project.`,
        );
        error.code = 'PROJECT_DELETED_TOMBSTONE';
        error.retryable = false;
        error.operatorAction = 'Rescan Import Projects and import the folder as a new project.';
        throw error;
    }
    const existing = await _findExistingPending(projectId, phase, targetDb);
    if (existing) {
        // Idempotent — same project re-queue returns the existing row. Incoming
        // blanks are treated as absent so a sidebar retry cannot erase the
        // durable name/path/QR snapshot captured during materialisation.
        // BRIEF C6: a released staged row (stagedAt set) carries a HAND-PICKED
        // curation. Only the staging surface (asStaged) may replace it — a
        // legacy caller handing in a scan-recomputed selection is exactly the
        // clobber the staging model forbids, so it is dropped, not applied.
        const selectionTrusted = !existing.stagedAt || opts.asStaged === true;
        if (opts.selectedFileIds !== undefined && selectionTrusted) {
            await targetDb.batch_jobs.update(existing.id, { selectedFileIds });
            existing.selectedFileIds = selectedFileIds;
        } else if (opts.selectedFileIds !== undefined) {
            console.debug(`[batch-queue] enqueue: kept curated selection on released staged row for ${projectId} (untrusted caller update dropped)`);
        }
        if (opts.displayMeta !== undefined) {
            const nextDisplayMeta = opts.displayMeta && typeof opts.displayMeta === 'object'
                ? _mergeDisplayMetaNonBlank(existing.displayMeta, opts.displayMeta, projectId)
                : existing.displayMeta || null;
            await targetDb.batch_jobs.update(existing.id, { displayMeta: nextDisplayMeta });
            existing.displayMeta = nextDisplayMeta;
        }
        if (opts.restartLineage !== undefined) {
            const currentLineage = _restartLineageFromJob(existing, projectId);
            if (currentLineage
                && currentLineage.restartOfProjectId !== restartLineage?.restartOfProjectId) {
                const error = new Error(
                    `enqueue: project ${projectId} already has a different restart predecessor in queue history`,
                );
                error.code = 'PROJECT_RESTART_LINEAGE_CONFLICT';
                error.retryable = false;
                throw error;
            }
            if (restartLineage) {
                await targetDb.batch_jobs.update(existing.id, restartLineage);
                Object.assign(existing, restartLineage);
            }
        }
        return { job: existing, created: false };
    }
    // S2 (2026-08-19, SPEC §7 blast-radius): a staged/waiting-files row is
    // the SAME piece of work — a legacy enqueue converts it in place instead
    // of minting a sibling pending row. The row's curated selectedFileIds are
    // TRUSTED and deliberately survive (BRIEF C6): legacy callers like
    // toggleProjectInQueue recompute selection from the current scan, and
    // letting that overwrite the operator's hand-picked staging selection is
    // exactly the clobber the staging UI exists to prevent.
    const staged = await _findStagedRow(projectId, phase, targetDb);
    if (staged) {
        // S5 (asStaged): the STAGING surface re-curating an already-staged
        // project updates the row in place — its selection is trusted.
        if (opts.asStaged) {
            const patch = {
                ...(opts.selectedFileIds !== undefined ? { selectedFileIds } : {}),
                ...(opts.displayMeta !== undefined && opts.displayMeta && typeof opts.displayMeta === 'object'
                    ? { displayMeta: _mergeDisplayMetaNonBlank(staged.displayMeta, opts.displayMeta, projectId) }
                    : {}),
            };
            if (Object.keys(patch).length > 0) await targetDb.batch_jobs.update(staged.id, patch);
            return { job: { ...staged, ...patch }, created: false, stagedInPlace: true };
        }
        const conversion = {
            status: 'pending',
            position: await _nextPendingPosition(phase, targetDb),
            enqueuedAt: _nowIso(),
            ...(staged.status === 'waiting-files' && staged.filesComplete === false
                ? { queueAnywayAt: _nowIso() }
                : {}),
            // displayMeta may legitimately gain non-blank fields.
            ...(opts.displayMeta !== undefined && opts.displayMeta && typeof opts.displayMeta === 'object'
                ? { displayMeta: _mergeDisplayMetaNonBlank(staged.displayMeta, opts.displayMeta, projectId) }
                : {}),
        };
        await targetDb.batch_jobs.update(staged.id, conversion);
        const job = { ...staged, ...conversion };
        return { job, created: false, convertedFromStaged: true };
    }
    // S5 (asStaged): the materialise-then-stage flow threads through THIS
    // seam so the unsynced intake transaction atomically produces a STAGED
    // row — never a pending row that a live runner could claim in the gap
    // before a separate stage flip (BRIEF C1/C6; SPEC §4).
    if (opts.asStaged) {
        const job = {
            projectId,
            batchId: null,
            phase,
            position: null,
            status: 'staged',
            stagedAt: _nowIso(),
            enqueuedAt: null,
            startedAt: null,
            completedAt: null,
            currentStage: null,
            retryCount: 0,
            lastError: null,
            lastErrorStage: null,
            lastErrorCode: null,
            lastErrorRetryable: null,
            lastErrorDetails: null,
            errorHistory: [],
            completionOutcome: null,
            reviewCode: null,
            reviewMessage: null,
            reviewStage: null,
            reviewDetails: null,
            reviewHistory: [],
            elapsedMs: 0,
            selectedFileIds,
            displayMeta: displayMeta || null,
            filesComplete: true,
            excludedRelPaths: [],
            owedNote: null,
            waitingPacks: null,
            autoQueueQuietMins: null,
            lastArrivalAt: null,
            arrivalRelPaths: [],
            includedRelPaths: [],
            queueAnywayAt: null,
            clearedAt: null,
            tabOrder: null,
            ...(restartLineage || {}),
        };
        const id = await targetDb.batch_jobs.add(job);
        job.id = id;
        return { job, created: true, createdStaged: true };
    }
    const position = await _nextPendingPosition(phase, targetDb);
    const job = {
        projectId,
        batchId: null,
        phase,
        position,
        status: 'pending',
        enqueuedAt: _nowIso(),
        startedAt: null,
        completedAt: null,
        currentStage: null,
        retryCount: 0,
        lastError: null,
        lastErrorStage: null,
        lastErrorCode: null,
        lastErrorRetryable: null,
        lastErrorDetails: null,
        errorHistory: [],
        completionOutcome: null,
        reviewCode: null,
        reviewMessage: null,
        reviewStage: null,
        reviewDetails: null,
        reviewHistory: [],
        elapsedMs: 0,
        selectedFileIds,
        displayMeta: displayMeta || null,
        ...(restartLineage || {}),
    };
    const id = await targetDb.batch_jobs.add(job);
    job.id = id;
    return { job, created: true };
}

function _emitCommittedEnqueue(result, prepared) {
    // S5: a row created as STAGED announces itself as staged, not enqueued —
    // it holds no queue position and must not trip start-on-enqueue listeners.
    if (result.createdStaged) {
        _emit('qm-batch-queue-staged', {
            jobId: result.job.id,
            projectId: prepared.projectId,
            phase: prepared.phase,
            selectedFileIds: result.job.selectedFileIds ?? null,
            displayMeta: result.job.displayMeta,
        });
        return;
    }
    if (result.stagedInPlace) return; // re-curation; liveQuery carries it
    // S2: a staged→pending conversion is an enqueue in every way that
    // matters to listeners — emit the same event (with the row's OWN
    // curated selection, not the caller's recomputed one).
    if (result.created || result.convertedFromStaged) {
        _emit('qm-batch-queue-enqueued', {
            jobId: result.job.id,
            projectId: prepared.projectId,
            phase: prepared.phase,
            position: result.job.position,
            batchId: null,
            selectedFileIds: result.convertedFromStaged
                ? (result.job.selectedFileIds ?? null)
                : prepared.selectedFileIds,
            displayMeta: result.job.displayMeta,
            ...(result.convertedFromStaged ? { fromStaged: true } : {}),
        });
    }
}

/**
 * Transaction-composition seam used by unsynced intake. The caller must own a
 * read/write Dexie transaction that includes batch_jobs; events are deliberately
 * deferred until that outer transaction commits.
 */
export async function _enqueueInCurrentTransaction(projectId, opts = {}) {
    const prepared = _prepareEnqueue(projectId, opts);
    const result = await _enqueuePrepared(prepared);
    return { ...result, prepared };
}

export function _notifyCommittedEnqueue(result) {
    if (result?.prepared) _emitCommittedEnqueue(result, result.prepared);
}

// W6 (Quote Brain DESIGN.md 2.12): a brain project is never queued in the
// browser. Its quote is the server run's; "Write the quote" starts one.
async function refuseBrainProjectEnqueue(projectId, targetDb) {
    let local = null;
    try { local = targetDb?.projects ? await targetDb.projects.get(projectId) : null; } catch { local = null; }
    if (isBrainProject(projectId, { scope: local?.scope })
        || isBrainPipelineVersion(local?.pipelineVersion ?? local?.pipeline_version)) {
        throw Object.assign(new Error('This quote is written by QuoteMaker, so it is not added to the Quote Run. Use Write the quote on the job instead.'), {
            code: 'QUOTE_BRAIN_PROJECT', retryable: false,
        });
    }
}

export async function enqueue(projectId, opts = {}) {
    await refuseBrainProjectEnqueue(projectId, opts?.db || db);
    const prepared = _prepareEnqueue(projectId, opts);
    const result = await _withEnqueueWriteLock(
        () => _enqueuePrepared(prepared),
        prepared.targetDb,
    );
    _emitCommittedEnqueue(result, prepared);
    return result.job;
}

/**
 * Create a fresh pending attempt from immutable cancelled/skipped history.
 * Deferred intake is copied inside the same transaction as the new row so a
 * stopped, not-yet-materialised folder never becomes a plain PROJECT_NOT_LOCAL
 * job. The old terminal row remains unchanged as queue audit history.
 *
 * @param {number} jobId
 * @param {{ selectedFileIds?: string[] | null, displayMeta?: object | null, db?: object }} [opts]
 * @returns {Promise<BatchJob>}
 */
export async function requeueTerminalJob(jobId, opts = {}) {
    const targetDb = opts?.db || db;
    if (!targetDb?.batch_jobs) {
        throw new Error('requeueTerminalJob: batch_jobs store is unavailable');
    }
    const result = await _withEnqueueWriteLock(async () => {
        const historical = await targetDb.batch_jobs.get(jobId);
        if (!historical) throw new Error(`requeueTerminalJob: job ${jobId} not found`);
        if (historical.dismissedAt) {
            throw Object.assign(new Error('This job was removed from the run list. Add the project again from Import Projects to run it.'), {
                code: 'QUEUE_JOB_DISMISSED', retryable: false,
            });
        }
        if (!['cancelled', 'skipped'].includes(historical.status)) {
            throw new Error(
                `requeueTerminalJob: job ${jobId} status is '${historical.status}', only cancelled or skipped history can be requeued`,
            );
        }
        if (isProjectDeletionTombstone(historical)) {
            const error = new Error(
                `Project ${historical.projectId} was deleted and cannot be re-queued under the retired UUID.`,
            );
            error.code = 'PROJECT_DELETED_TOMBSTONE';
            error.retryable = false;
            error.operatorAction = 'Rescan Import Projects and import the folder as a new project.';
            throw error;
        }

        const phase = historical.phase || 'prepare-quote';
        const deferredIntake = historical.deferredIntake || null;
        if (deferredIntake && phase !== 'pre-processing') {
            const error = new Error('Deferred intake can only be requeued into pre-processing.');
            error.code = 'DEFERRED_INTAKE_PHASE_INVALID';
            error.retryable = false;
            throw error;
        }

        if (deferredIntake) {
            const sourceKey = String(
                deferredIntake.sourceKey || historical.intakeSourceKey || '',
            ).trim();
            if (!sourceKey) {
                const error = new Error('The stopped queue job no longer has a durable source-folder identity.');
                error.code = 'DEFERRED_INTAKE_SOURCE_MISSING';
                error.retryable = true;
                error.operatorAction = 'Rescan the folder in Import Projects before queueing it again.';
                throw error;
            }
            const reservation = (await targetDb.batch_jobs.toArray()).find(candidate => (
                candidate?.id !== historical.id
                && !candidate?.dismissedAt
                && String(candidate?.deferredIntake?.sourceKey || candidate?.intakeSourceKey || '').trim() === sourceKey
                && (candidate.phase || 'prepare-quote') === 'pre-processing'
                && ['pending', 'processing', 'failed'].includes(candidate.status)
            ));
            if (reservation) {
                if (String(reservation.projectId || '') === String(historical.projectId || '')
                    && ['pending', 'processing'].includes(reservation.status)
                    && reservation.deferredIntake) {
                    return { job: reservation, prepared: null };
                }
                const error = new Error(
                    'Another queue attempt already owns this deferred source folder.',
                );
                error.code = 'DEFERRED_SOURCE_RESERVED';
                error.retryable = false;
                error.operatorAction = 'Open the preparation queue and continue with the existing folder reservation.';
                throw error;
            }
        }

        const displayMeta = opts.displayMeta !== undefined
            ? _mergeDisplayMetaNonBlank(
                historical.displayMeta,
                opts.displayMeta,
                historical.projectId,
            )
            : historical.displayMeta || null;
        // A deferred row must replay the exact approved snapshot. A current
        // folder rescan may have different IDs, so caller scope overrides are
        // deliberately ignored until the user performs an explicit rescan.
        const selectedFileIds = deferredIntake
            ? (historical.selectedFileIds || null)
            : (opts.selectedFileIds !== undefined
                ? opts.selectedFileIds
                : historical.selectedFileIds || null);
        const prepared = _prepareEnqueue(historical.projectId, {
            db: targetDb,
            phase,
            selectedFileIds,
            displayMeta,
            restartLineage: _restartLineageFromJob(historical),
        });
        const created = await _enqueuePrepared(prepared);
        if (deferredIntake && created.created) {
            const deferredPatch = {
                deferredIntake,
                intakeSourceKey: String(
                    historical.intakeSourceKey || deferredIntake.sourceKey || '',
                ).trim(),
                intakeSourcePath: String(
                    historical.intakeSourcePath || deferredIntake.sourcePath || '',
                ).trim(),
                materializationState: 'queued',
                materializationStartedAt: null,
                materializedAt: null,
                requeuedFromJobId: historical.id,
            };
            await targetDb.batch_jobs.update(created.job.id, deferredPatch);
            Object.assign(created.job, deferredPatch);
        } else if (created.created) {
            await targetDb.batch_jobs.update(created.job.id, {
                requeuedFromJobId: historical.id,
            });
            created.job.requeuedFromJobId = historical.id;
        }
        return { job: created.job, prepared, enqueueResult: created };
    }, targetDb);

    if (result.prepared && result.enqueueResult) {
        _emitCommittedEnqueue(result.enqueueResult, result.prepared);
    }
    return result.job;
}

/**
 * Remove a pending job from the queue. Idempotent — if the job doesn't
 * exist or is no longer pending, returns silently (the caller's desired
 * end state "this job is not pending in the queue" is already achieved).
 *
 * v5.37.23 (Peter 2026-05-20): pre-fix this threw on both missing job AND
 * non-pending status. The error path then surfaced "BLOCKED Could not
 * update queue: dequeue: job N not found" toast over and over in Stage 1
 * Data Entry — caused when the React queue state held a stale reference
 * to a job that another tab / the processing loop had already removed.
 * Each render → toggle → throw cycle showed the toast again. Making
 * dequeue idempotent + matching the semantic intent ("ensure not in
 * queue", not "perform a delete-if-found operation") stops the loop.
 *
 * Returns:
 *   { removed: true, status: 'pending' }   — job was pending + got deleted
 *   { removed: false, reason: 'not-found' } — job didn't exist (no-op)
 *   { removed: false, reason: 'status', status: '<actual>' } — not pending (no-op)
 *
 * @param {number} jobId
 */
export async function dequeue(jobId) {
    let committedProjectId = null;
    const result = await _withEnqueueWriteLock(async () => {
        const job = await db.batch_jobs.get(jobId);
        if (!job) {
            // Already gone — caller's intent is satisfied. Log so we don't
            // lose visibility on races, but don't throw.
            console.debug(`[batch-queue] dequeue: job ${jobId} not found (idempotent no-op)`);
            return { removed: false, reason: 'not-found' };
        }
        // Project-deletion rows are permanent audit tombstones. Even a
        // malformed or legacy row that still says "pending" must not be
        // removable, because enqueue() relies on this history to reject the
        // retired project UUID.
        if (isProjectDeletionTombstone(job)) {
            console.debug(`[batch-queue] dequeue: job ${jobId} is a project-deletion tombstone (protected no-op)`);
            return { removed: false, reason: 'project-deleted', status: job.status };
        }
        if (job.status !== 'pending') {
            // A runner may have claimed the row after the remove surface last
            // rendered. The exact read and mutation share this rw transaction,
            // so processing + its lease always win across tabs.
            console.debug(`[batch-queue] dequeue: job ${jobId} status is '${job.status}' (idempotent no-op)`);
            return { removed: false, reason: 'status', status: job.status };
        }
        committedProjectId = job.projectId || null;
        // A pending row that came through staging (stagedAt set) carries the
        // operator's hand-picked curation, exclusions and arrival audit. Every
        // legacy remove surface lands here, so it RETURNS TO STAGED in place.
        if (job.stagedAt) {
            const returnsToWaiting = job.filesComplete === false;
            const updated = await db.batch_jobs.update(jobId, {
                status: returnsToWaiting ? 'waiting-files' : 'staged',
                position: null,
                batchId: null,
                ...(returnsToWaiting ? { queueAnywayAt: null } : {}),
            });
            if (updated !== 1) return { removed: false, reason: 'not-found' };
            await _repackPendingPositions(db);
            return { removed: true, status: 'pending', returnedToStaged: true };
        }
        await db.batch_jobs.delete(jobId);
        // Re-pack positions of remaining pending jobs inside the same commit.
        await _repackPendingPositions(db);
        return { removed: true, status: 'pending' };
    }, db);

    // Advisory events describe committed state only. A rollback or a runner
    // that won the transaction race emits no stale dequeue signal.
    if (result.removed && result.returnedToStaged) {
        _emit('qm-batch-queue-unqueued', { jobId, projectId: committedProjectId });
        _emit('qm-batch-queue-dequeued', { jobId, projectId: committedProjectId, returnedToStaged: true });
    } else if (result.removed) {
        // S1 (2026-08-19): non-React listeners still receive a dequeue signal;
        // Dexie liveQuery remains the queue UI's source of truth.
        _emit('qm-batch-queue-dequeued', { jobId, projectId: committedProjectId });
    }
    return result;
}

// DEC-TQ-001: positions are per-phase. Repack iterates each phase
// independently so removing a pre-processing row doesn't shuffle
// prepare-quote positions (and vice-versa).
async function _repackPendingPositions(targetDb = db) {
    for (const phase of ['pre-processing', 'prepare-quote']) {
        const pending = await targetDb.batch_jobs
            .where('status').equals('pending')
            .and(r => (r.phase || 'prepare-quote') === phase)
            .sortBy('position');
        let pos = 1;
        for (const row of pending) {
            if (row.position !== pos) {
                await targetDb.batch_jobs.update(row.id, { position: pos });
            }
            pos += 1;
        }
    }
}

/**
 * Move a pending job to a new position (1-based) within its phase. Throws
 * if the job isn't 'pending' or position is out of range. Cross-phase
 * reorder is NOT supported — pre-processing rows can only reorder among
 * pre-processing rows, etc.
 *
 * @param {number} jobId
 * @param {number} newPosition - 1-based
 */
export async function reorder(jobId, newPosition) {
    return _withEnqueueWriteLock(async () => {
        const job = await db.batch_jobs.get(jobId);
        if (!job) throw new Error(`reorder: job ${jobId} not found`);
        if (job.status !== 'pending') {
            throw new Error(`reorder: job ${jobId} status is '${job.status}', only 'pending' can be reordered`);
        }
        const phase = job.phase || 'prepare-quote';
        const pending = await db.batch_jobs
            .where('status').equals('pending')
            .and(r => (r.phase || 'prepare-quote') === phase)
            .sortBy('position');
        const total = pending.length;
        const target = Math.max(1, Math.min(total, Number(newPosition) || 1));
        // The whole ordered rewrite is one commit. queueStaged must never see
        // a half-reordered list and allocate the same next position as a row
        // whose final position has not been written yet.
        const ordered = pending.filter(r => r.id !== jobId);
        ordered.splice(target - 1, 0, job);
        let pos = 1;
        for (const row of ordered) {
            if (row.position !== pos) {
                await db.batch_jobs.update(row.id, { position: pos });
            }
            pos += 1;
        }
    }, db);
}

// ─── S2 (2026-08-19): STAGING TRANSITIONS (docs/import-staging/SPEC.md §7) ──
// A 'staged' row is a declared intent with a curated file selection; a
// 'waiting-files' row (S4) is a staged row whose reference packs are still
// arriving. Both are PRE-pending: every runner, hydrate, resume and position
// path in this file selects status === 'pending' explicitly, so these rows
// are inert by construction. One row per project per phase carries staging,
// queueing and run state — there is deliberately NO sibling store to drift
// against (BRIEF C4). New fields are non-indexed, so no Dexie version bump
// is needed (deviation from SPEC §7's "version bump" note: a no-op bump
// would add migration noise for zero benefit; the status index already
// serves the new values).

const STAGED_STATUSES = ['staged', 'waiting-files'];
const _isStagedStatus = (status) => STAGED_STATUSES.includes(status);

async function _findStagedRow(projectId, phase = 'prepare-quote', targetDb = db) {
    if (!targetDb?.batch_jobs) return null;
    // Tombstoned rows are invisible to every staging transition: queueStaged
    // must not flip a deleted project back to a runnable status, and
    // removeStaged must not delete the audit row enqueue's resurrection gate
    // reads. Same invariant dequeue() enforces for pending rows.
    const rows = await targetDb.batch_jobs
        .where('projectId').equals(projectId)
        .and(row => _isStagedStatus(row.status)
            && (row.phase || 'prepare-quote') === phase
            && !isProjectDeletionTombstone(row))
        .toArray();
    return rows.length > 0 ? rows[0] : null;
}

/**
 * Stage a project: create (or re-curate) the single staged row that carries
 * the operator's TRUSTED file selection (BRIEF C6). The staging surface is
 * the curator — unlike enqueue's legacy callers, selection passed here always
 * wins. Staging an already-queued/running project never demotes it; the call
 * reports `alreadyQueued` and leaves the active row untouched.
 *
 * The caller owns materialisation: `projectId` must already be a Dexie
 * project row (SPEC §7 — collapseQueueJobs drops rows without a projectId).
 * No server probe happens here (C10).
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote', selectedFileIds?: string[] | null,
 *           displayMeta?: object | null, excludedRelPaths?: string[],
 *           filesComplete?: boolean, tabOrder?: number | null }} [opts]
 * @returns {Promise<{ job: BatchJob, created: boolean, alreadyQueued?: boolean }>}
 */
export async function stageProject(projectId, opts = {}) {
    const prepared = _prepareEnqueue(projectId, opts);
    // Same check-then-add race as enqueue: two near-simultaneous callers can
    // each observe "no staged row" and mint duplicates. Serialise through the
    // same write lock (module serial promise + one rw transaction).
    const result = await _withEnqueueWriteLock(
        () => _stageProjectPrepared(prepared),
        prepared.targetDb,
    );
    if (result.created) {
        _emit('qm-batch-queue-staged', {
            jobId: result.job.id,
            projectId,
            phase: prepared.phase,
            selectedFileIds: result.job.selectedFileIds ?? null,
            displayMeta: result.job.displayMeta || null,
        });
    }
    return result;
}

async function _stageProjectPrepared(prepared) {
    const { projectId, opts, phase, selectedFileIds, displayMeta, targetDb } = prepared;

    const history = await _readProjectQueueHistory(projectId, targetDb);
    if (history.some(isProjectDeletionTombstone)) {
        const error = new Error(
            `Project ${projectId} was deleted and cannot be staged under the retired UUID. Import its source folder again to create a fresh project.`,
        );
        error.code = 'PROJECT_DELETED_TOMBSTONE';
        error.retryable = false;
        throw error;
    }

    const active = await _findExistingPending(projectId, phase, targetDb);
    if (active) {
        // Queued or running — staging must not demote live work. Selection
        // updates for a QUEUED row are legitimate ("Changes apply because it
        // has not started", SPEC §3) but locked once processing.
        if (active.status === 'pending' && opts.selectedFileIds !== undefined) {
            await targetDb.batch_jobs.update(active.id, { selectedFileIds });
            active.selectedFileIds = selectedFileIds;
        }
        return { job: active, created: false, alreadyQueued: true };
    }

    const existingStaged = await _findStagedRow(projectId, phase, targetDb);
    if (existingStaged) {
        const patch = {};
        if (opts.selectedFileIds !== undefined) patch.selectedFileIds = selectedFileIds;
        if (opts.excludedRelPaths !== undefined) patch.excludedRelPaths = _normaliseRelPaths(opts.excludedRelPaths);
        if (opts.displayMeta !== undefined) {
            patch.displayMeta = opts.displayMeta && typeof opts.displayMeta === 'object'
                ? _mergeDisplayMetaNonBlank(existingStaged.displayMeta, opts.displayMeta, projectId)
                : existingStaged.displayMeta || null;
        }
        if (opts.tabOrder !== undefined) patch.tabOrder = opts.tabOrder;
        if (Object.keys(patch).length > 0) {
            await targetDb.batch_jobs.update(existingStaged.id, patch);
            Object.assign(existingStaged, patch);
        }
        return { job: existingStaged, created: false };
    }

    const job = {
        projectId,
        batchId: null,
        phase,
        position: null,               // staged rows hold no queue position
        status: 'staged',
        stagedAt: _nowIso(),
        enqueuedAt: null,
        startedAt: null,
        completedAt: null,
        currentStage: null,
        retryCount: 0,
        lastError: null,
        lastErrorStage: null,
        lastErrorCode: null,
        lastErrorRetryable: null,
        lastErrorDetails: null,
        errorHistory: [],
        completionOutcome: null,
        reviewCode: null,
        reviewMessage: null,
        reviewStage: null,
        reviewDetails: null,
        reviewHistory: [],
        elapsedMs: 0,
        selectedFileIds,
        displayMeta: displayMeta || null,
        filesComplete: opts.filesComplete !== false,
        excludedRelPaths: _normaliseRelPaths(opts.excludedRelPaths),
        owedNote: null,
        waitingPacks: null,
        autoQueueQuietMins: null,
        lastArrivalAt: null,
        arrivalRelPaths: [],
        includedRelPaths: [],
        queueAnywayAt: null,
        clearedAt: null,
        tabOrder: Number.isFinite(opts.tabOrder) ? opts.tabOrder : null,
    };
    const id = await targetDb.batch_jobs.add(job);
    job.id = id;
    return { job, created: true };
}

function _normaliseRelPaths(value) {
    if (!Array.isArray(value)) return [];
    return Array.from(new Set(value.map((entry) => String(entry || '').trim()).filter(Boolean)));
}

/**
 * Release a staged row into the pending queue. The curated selection rides
 * unchanged — this is a status flip, never a re-curation. Releasing a
 * 'waiting-files' row before all packs arrived stamps `queueAnywayAt` so the
 * card can say so honestly (SPEC §5).
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote' }} [opts]
 * @returns {Promise<{ job: BatchJob, queued: boolean, alreadyQueued?: boolean }>}
 */
export async function queueStaged(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;

    const result = await _withEnqueueWriteLock(
        () => _queueStagedInCurrentTransaction(projectId, phase, targetDb),
        targetDb,
    );
    if (result.queued) _emitQueuedFromStaged(result.job, projectId, phase);
    return result;
}

async function _queueStagedInCurrentTransaction(projectId, phase, targetDb, {
    markFilesComplete = false,
} = {}) {
    // The deletion marker is a project-history invariant, not merely a flag
    // on whichever staged row happened to be selected. Legacy/partially
    // repaired stores can contain a stale staged sibling beside the permanent
    // tombstone; releasing that sibling would resurrect the retired UUID.
    const projectHistory = await _readProjectQueueHistory(projectId, targetDb);
    const staged = await _findStagedRow(projectId, phase, targetDb);
    const projectWasDeleted = projectHistory.some(isProjectDeletionTombstone);
    if (projectWasDeleted && staged) {
        const error = new Error(
            `Project ${projectId} was deleted and cannot be queued under the retired UUID. Import its source folder again to create a fresh project.`,
        );
        error.code = 'PROJECT_DELETED_TOMBSTONE';
        error.retryable = false;
        throw error;
    }

    // Preserve the established idempotent surface contract for a tombstone
    // that is itself the only legacy "staged" row: it remains invisible and
    // reports no staged work. The stronger history error above is reserved for
    // the dangerous shape that contains a distinct runnable sibling.
    if (projectWasDeleted && !staged) {
        const error = new Error(`queueStaged: project ${projectId} has no staged ${phase} row`);
        error.code = 'NO_STAGED_ROW';
        error.retryable = false;
        throw error;
    }

    const active = await _findExistingPending(projectId, phase, targetDb);
    if (active) return { job: active, queued: false, alreadyQueued: true };

    if (!staged) {
        const error = new Error(`queueStaged: project ${projectId} has no staged ${phase} row`);
        error.code = 'NO_STAGED_ROW';
        error.retryable = false;
        throw error;
    }

    // The staged lookup and position scan above run inside the same rw
    // transaction as this exact status re-read and update. Across tabs,
    // IndexedDB serialises that boundary; within this tab, the module write
    // lock prevents two Menu actions from allocating the same position.
    const current = await targetDb.batch_jobs.get(staged.id);
    const transitionable = current
        && current.projectId === projectId
        && (current.phase || 'prepare-quote') === phase
        && _isStagedStatus(current.status)
        && !isProjectDeletionTombstone(current);
    if (!transitionable) {
        const error = new Error(`queueStaged: staged ${phase} row changed before it could be queued`);
        error.code = 'STAGED_TRANSITION_LOST';
        error.retryable = true;
        throw error;
    }
    if (markFilesComplete && (current.arrivalRelPaths || []).length > 0) {
        return { job: current, queued: false, reason: 'unreviewed-arrivals' };
    }

    const now = _nowIso();
    const patch = {
        status: 'pending',
        position: await _nextPendingPosition(phase, targetDb),
        enqueuedAt: now,
        ...(markFilesComplete ? { filesComplete: true } : {}),
        ...(current.status === 'waiting-files' && current.filesComplete === false && !markFilesComplete
            ? { queueAnywayAt: now }
            : {}),
    };
    const updated = await targetDb.batch_jobs.update(current.id, patch);
    if (updated !== 1) {
        const error = new Error(`queueStaged: staged ${phase} row disappeared before commit`);
        error.code = 'STAGED_TRANSITION_LOST';
        error.retryable = true;
        throw error;
    }
    _leaveActiveRun(current.id);
    return { job: { ...current, ...patch }, queued: true };
}

/**
 * Quote Run: a job put back on the run list while a run goes is not one of
 * that run's jobs, even when it keeps the id the run froze (Leave out, then
 * Add to the run). It waits for the next run: this run never claims it and
 * its Stop never cancels it. getQueueStatus cohortJobIds stops naming it.
 */
function _leaveActiveRun(jobId) {
    if (!_isActive && !_isAllQueueActive) return;
    _leftRunJobIds.add(jobId);
    _activeCohortJobIds.delete(jobId);
    _activeAllCohortJobIds.delete(jobId);
}

function _emitQueuedFromStaged(job, projectId, phase) {
    _emit('qm-batch-queue-enqueued', {
        jobId: job.id,
        projectId,
        phase,
        position: job.position,
        batchId: null,
        selectedFileIds: job.selectedFileIds ?? null,
        displayMeta: job.displayMeta || null,
        fromStaged: true,
    });
}

/**
 * Take a pending job back to 'staged' IN PLACE — the SPEC's "Remove from
 * queue" (§3): curation, display meta and history stay on the same row; only
 * membership in the run order changes. This is deliberately NOT dequeue()'s
 * pending-only DELETE, which would discard the curated selection.
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote' }} [opts]
 * @returns {Promise<{ job: BatchJob | null, changed: boolean, reason?: string }>}
 */
export async function unqueueToStaged(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;

    const result = await _withEnqueueWriteLock(async () => {
        let candidate = await _findExistingPending(projectId, phase, targetDb);
        // Phase-agnostic fallback for pending jobs:
        if (!candidate) {
            const alternatePhase = phase === 'pre-processing' ? 'prepare-quote' : 'pre-processing';
            candidate = await _findExistingPending(projectId, alternatePhase, targetDb);
        }

        if (candidate) {
            // Re-read inside the same rw transaction immediately before mutation.
            // A runner may have claimed the candidate after a UI snapshot was
            // rendered; processing + its lease must always win over "Take off run".
            const current = await targetDb.batch_jobs.get(candidate.id);
            const currentMatches = current
                && current.projectId === projectId
                && (current.phase || 'prepare-quote') === (candidate.phase || 'prepare-quote');
            if (!currentMatches || current.status !== 'pending') {
                const running = currentMatches && current.status === 'processing';
                return {
                    job: currentMatches ? current : null,
                    changed: false,
                    reason: running ? 'running' : 'not-queued',
                };
            }

            // A row released early ("queue anyway": filesComplete still false)
            // goes back to WAITING-FILES, not plain staged — the operator's
            // declared gate survives the round trip.
            const returnsToWaiting = current.filesComplete === false;
            const patch = {
                status: returnsToWaiting ? 'waiting-files' : 'staged',
                position: null,
                batchId: null,
                stagedAt: current.stagedAt || _nowIso(),
                ...(returnsToWaiting ? { queueAnywayAt: null } : {}),
            };
            const updated = await targetDb.batch_jobs.update(current.id, patch);
            if (updated !== 1) return { job: null, changed: false, reason: 'not-queued' };
            await _repackPendingPositions(targetDb);
            return {
                job: { ...current, ...patch },
                changed: true,
                effectivePhase: candidate.phase || 'prepare-quote',
            };
        }

        // If no pending candidate exists in either phase, check if the project
        // is considered queued on the run list due to a completed intake
        // without downstream prepare-quote lineage.
        const history = await targetDb.batch_jobs.where('projectId').equals(projectId).toArray();
        if (history.some(isProjectDeletionTombstone)) {
            return { job: null, changed: false, reason: 'project-deleted' };
        }

        // Never unqueue a running job
        const runningJob = history.find(r => r.status === 'processing' && !r.dismissedAt);
        if (runningJob) {
            return { job: runningJob, changed: false, reason: 'running' };
        }

        // Check if there is a completed intake row awaiting prepare-quote
        const completedIntake = history.find(r =>
            (r.phase || 'prepare-quote') === 'pre-processing'
            && r.status === 'completed'
            && !r.dismissedAt
        );

        if (completedIntake) {
            // Check if there's already a staged prepare-quote row
            const existingStaged = history.find(r =>
                (r.phase || 'prepare-quote') === 'prepare-quote'
                && _isStagedStatus(r.status)
                && !r.dismissedAt
            );
            if (existingStaged) {
                return { job: existingStaged, changed: true, effectivePhase: 'prepare-quote' };
            }

            const now = _nowIso();
            const stagedJob = {
                projectId,
                batchId: null,
                phase: 'prepare-quote',
                position: null,
                status: 'staged',
                stagedAt: now,
                enqueuedAt: null,
                startedAt: null,
                completedAt: null,
                currentStage: null,
                retryCount: 0,
                lastError: null,
                lastErrorStage: null,
                lastErrorCode: null,
                lastErrorRetryable: null,
                lastErrorDetails: null,
                errorHistory: [],
                completionOutcome: null,
                reviewCode: null,
                reviewMessage: null,
                reviewStage: null,
                reviewDetails: null,
                reviewHistory: [],
                elapsedMs: 0,
                selectedFileIds: completedIntake.selectedFileIds || null,
                displayMeta: completedIntake.displayMeta || null,
                filesComplete: true,
                excludedRelPaths: completedIntake.excludedRelPaths || [],
                owedNote: null,
                waitingPacks: null,
                autoQueueQuietMins: null,
                lastArrivalAt: null,
                arrivalRelPaths: [],
                includedRelPaths: [],
                queueAnywayAt: null,
                clearedAt: null,
                tabOrder: completedIntake.tabOrder ?? null,
            };
            const id = await targetDb.batch_jobs.add(stagedJob);
            stagedJob.id = id;
            await _repackPendingPositions(targetDb);
            return { job: stagedJob, changed: true, effectivePhase: 'prepare-quote' };
        }

        return { job: null, changed: false, reason: 'not-queued' };
    }, targetDb);

    // Events describe committed state only; never emit from inside the
    // transaction because a later write/repack failure would roll it back.
    if (result.changed) {
        _emit('qm-batch-queue-unqueued', {
            jobId: result.job.id,
            projectId,
            phase: result.effectivePhase || phase,
        });
    }
    return result;
}

// ─── S4 (2026-08-19): UPLOAD GATE (docs/import-staging/SPEC.md §5) ──────────
// A 'waiting-files' row is a staged row whose reference packs are still
// arriving from the site assessor / office staff. The gate is DECLARED by the
// operator (never inferred from folder quiescence — guessing is how buttons
// come to lie), arrivals are RECORDED against it, and it is RELEASED either
// by the operator ("All files in") or explicitly queued anyway. The quiet
// timer fields are stored here; the timer itself is a surface concern (S5).

/**
 * Declare that a staged project is still waiting on reference files.
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote',
 *           waitingPacks?: Array<{relPrefix: string, label: string, expectation: 'in'|'waiting'|'not-needed'}>,
 *           owedNote?: string | null, autoQueueQuietMins?: number | null }} [opts]
 * @returns {Promise<{ job: BatchJob, changed: boolean, reason?: string }>}
 */
export async function markWaitingFiles(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;
    const result = await _withEnqueueWriteLock(async () => {
        const staged = await _findStagedRow(projectId, phase, targetDb);
        if (!staged) return { job: null, changed: false, reason: 'not-staged' };
        const patch = {
            status: 'waiting-files',
            filesComplete: false,
            waitingPacks: Array.isArray(opts.waitingPacks)
                ? opts.waitingPacks.map((pack) => ({
                    relPrefix: String(pack?.relPrefix || '').trim(),
                    label: String(pack?.label || pack?.relPrefix || '').trim(),
                    expectation: ['in', 'waiting', 'not-needed'].includes(pack?.expectation) ? pack.expectation : 'waiting',
                    firstArrivalAt: pack?.firstArrivalAt || null,
                })).filter((pack) => pack.relPrefix)
                : (staged.waitingPacks || null),
            owedNote: opts.owedNote !== undefined ? (opts.owedNote || null) : (staged.owedNote ?? null),
            autoQueueQuietMins: opts.autoQueueQuietMins !== undefined
                ? (Number.isFinite(opts.autoQueueQuietMins) ? opts.autoQueueQuietMins : null)
                : (staged.autoQueueQuietMins ?? null),
        };
        const updated = await targetDb.batch_jobs.update(staged.id, patch);
        if (updated !== 1) return { job: null, changed: false, reason: 'not-staged' };
        return { job: { ...staged, ...patch }, changed: true };
    }, targetDb);

    // Publish only after the rw transaction has committed. A stale tab that
    // loses to queue/retirement must not announce a waiting-files state that
    // never became durable.
    if (result.changed) {
        _emit('qm-batch-queue-waiting-files', { jobId: result.job.id, projectId, phase });
    }
    return result;
}

/**
 * Record newly arrived files against a waiting project. Arrivals land in the
 * review tray (`arrivalRelPaths`) — they are NOT auto-included in the curated
 * selection; the operator decides via reviewArrival.
 *
 * @param {string} projectId
 * @param {{ relPaths?: string[], phase?: 'pre-processing' | 'prepare-quote' }} [opts]
 * @returns {Promise<{ job: BatchJob | null, recorded: number }>}
 */
export async function recordArrival(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;
    const relPaths = _normaliseRelPaths(opts.relPaths);
    if (relPaths.length === 0) return { job: null, recorded: 0 };
    return _withEnqueueWriteLock(async () => {
        const row = await _findStagedRow(projectId, phase, targetDb);
        if (!row) return { job: null, recorded: 0 };
        const now = _nowIso();
        const existing = new Set([
            ...(row.arrivalRelPaths || []),
            ...(row.excludedRelPaths || []),
            ...(row.includedRelPaths || []),
            ...(row.selectedFileIds || []),
        ]);
        const fresh = relPaths.filter((relPath) => !existing.has(relPath));
        // S5 loop guard: a no-op arrival must NOT touch the row. Surfaces feed
        // this from scan effects that re-run on every liveQuery emission — an
        // unconditional write here would re-emit forever.
        if (fresh.length === 0) return { job: row, recorded: 0 };
        const waitingPacks = Array.isArray(row.waitingPacks)
            ? row.waitingPacks.map((pack) => (
                !pack.firstArrivalAt && fresh.some((relPath) => relPath.startsWith(pack.relPrefix))
                    ? { ...pack, firstArrivalAt: now }
                    : pack
            ))
            : row.waitingPacks;
        const patch = {
            arrivalRelPaths: [...(row.arrivalRelPaths || []), ...fresh],
            lastArrivalAt: now,
            waitingPacks,
        };
        const updated = await targetDb.batch_jobs.update(row.id, patch);
        if (updated !== 1) return { job: null, recorded: 0 };
        return { job: { ...row, ...patch }, recorded: fresh.length };
    }, targetDb);
}

/**
 * Review one arrival: include it in the curated selection or exclude it.
 * Either way it leaves the tray; the decision is durable and attributable
 * (operator-excluded is distinct from never-seen — SPEC §4).
 *
 * @param {string} projectId
 * @param {{ relPath: string, decision: 'include' | 'exclude', fileId?: string | null,
 *           phase?: 'pre-processing' | 'prepare-quote' }} opts
 * @returns {Promise<{ job: BatchJob | null, changed: boolean, reason?: string }>}
 */
export async function reviewArrival(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;
    const relPath = String(opts.relPath || '').trim();
    const decision = opts.decision;
    if (!relPath || (decision !== 'include' && decision !== 'exclude')) {
        return { job: null, changed: false, reason: 'bad-args' };
    }
    return _withEnqueueWriteLock(async () => {
        const row = await _findStagedRow(projectId, phase, targetDb);
        if (!row) return { job: null, changed: false, reason: 'not-staged' };
        const pendingArrivals = row.arrivalRelPaths || [];
        // Review is a one-shot compare-and-mutate boundary. A second tab may
        // still display the same tray item after the first decision commits;
        // accepting that stale click would persist mutually contradictory
        // Include and Exclude decisions for one file.
        if (!pendingArrivals.includes(relPath)) {
            return { job: row, changed: false, reason: 'not-pending-review' };
        }
        const arrivalRelPaths = pendingArrivals.filter((entry) => entry !== relPath);
        const patch = { arrivalRelPaths };
        if (decision === 'include') {
            // The curated selection is keyed by the file's durable id — the caller
            // materialises the arrived file into the project row FIRST and passes
            // the id the run's scope filter will match (RunCard resolves this via
            // the explorer's folder access). Without one, the relPath itself is
            // the fallback key; the scope builder treats bare path-like keys as
            // relative paths so a later folder-sync import still matches.
            const key = String(opts.fileId || relPath);
            const selected = new Set(row.selectedFileIds || []);
            selected.add(key);
            patch.selectedFileIds = Array.from(selected);
            // The relPath is ALSO recorded durably: the arrival scan's loop guard
            // keys on relPaths, and an id-keyed include would otherwise let the
            // same file re-tray on every scan. Distinct from excluded (audit).
            patch.includedRelPaths = _normaliseRelPaths([...(row.includedRelPaths || []), relPath]);
        } else {
            patch.excludedRelPaths = _normaliseRelPaths([...(row.excludedRelPaths || []), relPath]);
        }
        const updated = await targetDb.batch_jobs.update(row.id, patch);
        if (updated !== 1) return { job: null, changed: false, reason: 'not-staged' };
        return { job: { ...row, ...patch }, changed: true };
    }, targetDb);
}

/**
 * The operator declares uploads finished: flips the waiting row straight to
 * the pending queue in one transition (SPEC §3 "All files in, queue project").
 * A leftover unreviewed tray refuses — reviewing arrivals IS the gate.
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote' }} [opts]
 * @returns {Promise<{ job: BatchJob | null, queued: boolean, reason?: string }>}
 */
export async function markFilesComplete(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;
    let result;
    try {
        result = await _withEnqueueWriteLock(
            () => _queueStagedInCurrentTransaction(projectId, phase, targetDb, { markFilesComplete: true }),
            targetDb,
        );
    } catch (error) {
        // Preserve the operator-facing contract for an already removed or
        // retired staging row. queueStaged is an imperative action and still
        // throws NO_STAGED_ROW; "All files in" is an idempotent surface action.
        if (error?.code === 'NO_STAGED_ROW') {
            return { job: null, queued: false, reason: 'not-staged' };
        }
        throw error;
    }
    if (result.queued) _emitQueuedFromStaged(result.job, projectId, phase);
    return result;
}

/**
 * S5: dismiss a reviewed draft's run-card ("Clear card", SPEC §3). Stamps
 * `clearedAt` on the newest completed prepare-quote row so the derivation
 * flips needs_review → ready and the tab disappears. The row itself stays —
 * it is history, not garbage.
 *
 * @param {string} projectId
 * @param {{ db?: object }} [opts]
 * @returns {Promise<{ job: BatchJob | null, cleared: boolean, reason?: string }>}
 */
export async function clearReviewedCard(projectId, opts = {}) {
    const targetDb = opts.db || db;
    if (!projectId || !targetDb?.batch_jobs) return { job: null, cleared: false, reason: 'bad-args' };
    const rows = await targetDb.batch_jobs
        .where('projectId').equals(projectId)
        .and(row => (row.phase || 'prepare-quote') === 'prepare-quote' && row.status === 'completed' && !row.clearedAt)
        .toArray();
    if (rows.length === 0) return { job: null, cleared: false, reason: 'nothing-to-clear' };
    const newest = rows.sort((a, b) => (
        (new Date(b?.completedAt || b?.enqueuedAt || 0).getTime() || 0)
        - (new Date(a?.completedAt || a?.enqueuedAt || 0).getTime() || 0)
        || Number(b?.id || 0) - Number(a?.id || 0)
    ))[0];
    const clearedAt = _nowIso();
    // Clear every uncleared completed row for the project, not only the
    // newest — a single Clear means "this project's review is done".
    for (const row of rows) {
        await targetDb.batch_jobs.update(row.id, { clearedAt });
    }
    _emit('qm-batch-queue-card-cleared', { jobId: newest.id, projectId });
    return { job: { ...newest, clearedAt }, cleared: true };
}

/**
 * S5: bulk dismiss reviewed draft run-cards. Stamps `clearedAt` on every
 * uncleared completed prepare-quote row.
 *
 * @param {string[]} [projectIds] - Optional list of projectIds to clear. If omitted or null, clears all.
 * @param {{ db?: object }} [opts]
 * @returns {Promise<{ count: number, projectIds: string[] }>}
 */
export async function clearAllReviewedCards(projectIds = null, opts = {}) {
    const targetDb = opts.db || db;
    if (!targetDb?.batch_jobs) return { count: 0, projectIds: [] };
    const allRows = await targetDb.batch_jobs.toArray();
    const idSet = Array.isArray(projectIds) && projectIds.length > 0 ? new Set(projectIds) : null;
    const rows = allRows.filter(row => (
        (row.phase || 'prepare-quote') === 'prepare-quote'
        && row.status === 'completed'
        && !row.clearedAt
        && (!idSet || idSet.has(row.projectId))
    ));
    if (rows.length === 0) return { count: 0, projectIds: [] };
    const clearedAt = _nowIso();
    const affectedProjectIds = new Set();
    for (const row of rows) {
        await targetDb.batch_jobs.update(row.id, { clearedAt });
        if (row.projectId) affectedProjectIds.add(row.projectId);
    }
    for (const pId of affectedProjectIds) {
        _emit('qm-batch-queue-card-cleared', { projectId: pId });
    }
    return { count: rows.length, projectIds: Array.from(affectedProjectIds) };
}

/**
 * Delete a staged/waiting-files row (the SPEC's destructive "Remove"). A
 * pending row must go through unqueueToStaged first — the button matrix never
 * offers Remove while queued, and the engine enforces the same.
 *
 * @param {string} projectId
 * @param {{ phase?: 'pre-processing' | 'prepare-quote' }} [opts]
 * @returns {Promise<{ removed: boolean, reason?: string }>}
 */
export async function removeStaged(projectId, opts = {}) {
    const phase = opts.phase || 'prepare-quote';
    const targetDb = opts.db || db;
    const result = await _withEnqueueWriteLock(async () => {
        let candidate = await _findStagedRow(projectId, phase, targetDb);
        if (!candidate) {
            const alternatePhase = phase === 'pre-processing' ? 'prepare-quote' : 'pre-processing';
            candidate = await _findStagedRow(projectId, alternatePhase, targetDb);
        }

        if (candidate) {
            // Re-read inside the transaction. A concurrent Queue action may have
            // moved this exact row to pending since the surface last rendered; a
            // pending/running row is never removable through the staging action.
            const current = await targetDb.batch_jobs.get(candidate.id);
            const removable = current
                && current.projectId === projectId
                && (current.phase || 'prepare-quote') === (candidate.phase || 'prepare-quote')
                && _isStagedStatus(current.status)
                && !isProjectDeletionTombstone(current);
            if (!removable) return { removed: false, reason: 'not-staged' };

            await targetDb.batch_jobs.delete(current.id);

            // Also dismiss any completed intake rows for this project,
            // so removing the staged setup does not cause completed intake history
            // to re-derive the project as 'queued' (on the run list) again.
            const relatedIntakes = (await targetDb.batch_jobs.where('projectId').equals(projectId).toArray())
                .filter(r => (r.phase || 'prepare-quote') === 'pre-processing' && !r.dismissedAt);
            const now = _nowIso();
            for (const intake of relatedIntakes) {
                await targetDb.batch_jobs.update(intake.id, { dismissedAt: now });
            }

            return { removed: true, jobId: current.id, effectivePhase: candidate.phase || phase };
        }

        // If no staged row exists, check if there's completed intake history that can be dismissed
        const history = await targetDb.batch_jobs.where('projectId').equals(projectId).toArray();
        if (history.some(isProjectDeletionTombstone)) {
            return { removed: false, reason: 'project-deleted' };
        }
        if (history.some(r => r.status === 'processing' && !r.dismissedAt)) {
            return { removed: false, reason: 'running' };
        }
        const undismissedIntakes = history.filter(r =>
            (r.phase || 'prepare-quote') === 'pre-processing'
            && ['completed', 'failed', 'cancelled', 'skipped'].includes(r.status)
            && !r.dismissedAt
        );
        if (undismissedIntakes.length > 0) {
            const now = _nowIso();
            for (const intake of undismissedIntakes) {
                await targetDb.batch_jobs.update(intake.id, { dismissedAt: now });
            }
            return { removed: true, jobId: undismissedIntakes[0].id, effectivePhase: phase };
        }

        return { removed: false, reason: 'not-staged' };
    }, targetDb);

    if (result.removed) {
        _emit('qm-batch-queue-staged-removed', {
            jobId: result.jobId,
            projectId,
            phase: result.effectivePhase || phase,
        });
        return { removed: true };
    }
    return result;
}

/**
 * List jobs. Optional filter by batchId, status array, or phase.
 *
 * @param {{ batchId?: string, status?: string[], phase?: 'pre-processing' | 'prepare-quote', includeDismissed?: boolean }} [opts]
 * @returns {Promise<BatchJob[]>}
 */
export async function listJobs(opts = {}) {
    let collection = db.batch_jobs.toCollection();
    const rows = await collection.toArray();
    return rows
        .filter(r => opts.includeDismissed === true || !r.dismissedAt)
        .filter(r => !opts.batchId || r.batchId === opts.batchId)
        .filter(r => !opts.status || opts.status.includes(r.status))
        .filter(r => !opts.phase || (r.phase || 'prepare-quote') === opts.phase)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
}

/**
 * Snapshot of queue state for UI consumption. Synchronous (returns the
 * in-memory snapshot of the active queue's transient flags). For row-level
 * detail, use `listJobs()`.
 *
 * @returns {{ active: boolean, paused: boolean, currentJobId: number|null, batchId: string|null, phase: string|null, stopAfterCurrent: boolean, runnerOwnerId: string|null, cohortJobIds: Array<number|string>, readingDone: boolean }}
 */
export function getQueueStatus() {
    return {
        active: _isActive || _isAllQueueActive,
        paused: _isPaused,
        currentJobId: _currentJobId,
        batchId: _activeBatchId || _activeAllRunId,
        phase: _activePhase || (_isAllQueueActive ? 'all' : null),
        // Only a running run can be armed to stop after its job.
        stopAfterCurrent: (_isActive || _isAllQueueActive) && _stopAfterCurrent,
        // Read only, for the Quote Run's words. While a run is going: the
        // runner id its claims carry (claimOwnerId), so a processing row this
        // page claimed can be told from one another tab or a closed page left,
        // and the jobs the run froze when it started, which are the only jobs
        // it will claim. A job put back after the start waits for the next run.
        runnerOwnerId: (_isActive || _isAllQueueActive) ? _runnerOwnerId : null,
        cohortJobIds: _isAllQueueActive
            ? Array.from(_activeAllCohortJobIds)
            : (_isActive ? Array.from(_activeCohortJobIds) : []),
        // True once this run will read no more files: a prepare-all run past
        // its reading phase (it never goes back to reading), or a run of
        // drafts only. A job put back while the run goes (queueStaged,
        // retryFailed) leaves the frozen jobs above and waits for the next run.
        readingDone: _isAllQueueActive
            ? _allRunReadingDone
            : (_isActive && _activePhase === 'prepare-quote'),
    };
}

// ─── PUBLIC API: lifecycle ─────────────────────────────────────────────────

// DEC-TQ-001: phase-aware module state. Tracks which phase the active
// queue is running so listJobs/getQueueStatus/clear can scope to it.
let _activePhase = null; // 'pre-processing' | 'prepare-quote' | null
// Quote Run: set once a prepare-all run is past its reading phase. Read only,
// through getQueueStatus (readingDone).
let _allRunReadingDone = false;

/**
 * Start the queue for a specific phase. Drives one project at a time
 * through the runner's stages array using the injected pipelineRunner.
 * Each pending job is atomically claimed and assigned to this run's batchId.
 * The run freezes its pending job IDs before the first claim. Jobs enqueued
 * afterward remain pending until a new explicit start.
 *
 * Strictly sequential per DEC-BQ-007 + DEC-TQ-002.
 *
 * @param {{ pipelineRunner: PipelineRunner, phase?: 'pre-processing' | 'prepare-quote', onProgress?: Function, skipOnFailure?: boolean, maxConcurrent?: number, jobIds?: number[], allRunId?: string|null }} opts
 *
 * The runner contract:
 *   - stages: array of stage identifiers (e.g. [1,2,3,4,5] for prepare-quote
 *     or ['preprocess'] for pre-processing). Defaults to [1..5] for
 *     backward-compat with the existing PipelineRunner.
 *   - loadProject(projectId): Promise<void>
 *   - saveProject(projectId): Promise<void>
 *   - runStage<Name>(projectId): Promise<void>  — one per stages[i]
 *     (e.g. runStage1, runStagePreprocess). The stage name in the call is
 *     the stages[i] value (capitalised first letter, kebab→camel).
 *   - markReadyForBuilderReview(projectId): Promise<void> for prepare-quote;
 *     markReadyForAnalysis(projectId): Promise<void> for pre-processing
 *   - phase: optional, runner can self-identify; if absent the opts.phase
 *     wins. Used for terminal-write callback selection.
 */
export async function startQueue({ pipelineRunner, phase = 'prepare-quote', onProgress, skipOnFailure = true, maxConcurrent = 1, guardUnsyncedFolders = false, jobIds = null, allRunId = null } = {}) {
    if (_isActive) {
        throw new Error('startQueue: queue is already active');
    }
    if (_isAllQueueActive && allRunId !== _activeAllRunId) {
        throw new Error('startQueue: an operator-confirmed prepare-all run is already active');
    }
    if (!pipelineRunner) {
        throw new Error('startQueue: pipelineRunner is required (injection contract per DEC-BQ-015)');
    }
    if (phase !== 'pre-processing' && phase !== 'prepare-quote') {
        throw new Error(`startQueue: phase must be 'pre-processing' or 'prepare-quote', got '${phase}'`);
    }
    // 2026-05-20: maxConcurrent honest-refusal. The processing loop is single-
    // threaded and pipeline-runner.js drives Stage 1→5 via React useEffects in
    // App.jsx that depend on shared single-project state (currentProjectId,
    // formData, scope, analysisReady, projectWMS, quoteData). Two workers
    // calling loadProject(P1) and loadProject(P2) would clobber that state
    // immediately. The Council's strict-sequential choice (DEC-BQ-007 +
    // DEC-TQ-002) is enforcing the architecture correctly — don't silently
    // accept N>1 and pretend it works. For parallel throughput today, open
    // multiple browser tabs. See CLAUDE.md "Batch queue concurrency" HARD RULE.
    if (maxConcurrent !== 1) {
        throw new Error(
            `startQueue: maxConcurrent must be 1 — single-project React state ` +
            `means parallel processing requires the pipeline-runner refactor. ` +
            `For parallel throughput today, open multiple browser tabs. ` +
            `See CLAUDE.md "Batch queue concurrency" HARD RULE.`
        );
    }

    // Resolve runner stages. Legacy runners (no `stages` array) default to
    // the historical [1..5] sequence so prior PipelineRunner instances keep
    // their behaviour without modification.
    const stages = Array.isArray(pipelineRunner.stages) && pipelineRunner.stages.length > 0
        ? pipelineRunner.stages
        : [1, 2, 3, 4, 5];

    // Validate the per-stage function presence + the loadProject/save pair.
    for (const fn of ['loadProject', 'saveProject']) {
        if (typeof pipelineRunner[fn] !== 'function') {
            throw new Error(`startQueue: pipelineRunner missing required function '${fn}'`);
        }
    }
    for (const stage of stages) {
        const fnName = _stageFunctionName(stage);
        if (typeof pipelineRunner[fnName] !== 'function') {
            throw new Error(`startQueue: pipelineRunner missing stage function '${fnName}' for stage '${stage}'`);
        }
    }
    // Terminal writer name: prepare-quote → markReadyForBuilderReview;
    // pre-processing → markReadyForAnalysis.
    const terminalFnName = phase === 'pre-processing' ? 'markReadyForAnalysis' : 'markReadyForBuilderReview';
    if (typeof pipelineRunner[terminalFnName] !== 'function') {
        throw new Error(`startQueue: pipelineRunner missing required function '${terminalFnName}' for phase '${phase}'`);
    }
    if (phase === 'prepare-quote') {
        for (const fn of ['acquire', 'renew', 'release']) {
            if (typeof pipelineRunner.quoteRunClaims?.[fn] !== 'function') {
                throw new Error(`startQueue: prepare-quote runner missing required Quote Run claim function '${fn}'`);
            }
        }
        if (typeof pipelineRunner.reconcileBackendProject !== 'function') {
            throw new Error('startQueue: prepare-quote runner missing required backend reconciliation');
        }
    }

    // Activate this tab's strictly-sequential runner. Individual jobs are
    // claimed atomically below rather than bulk-labelled up front; that is the
    // cross-tab boundary that prevents two tabs from running the same row.
    const batchId = _newBatchId();
    const ownerId = _getRunnerOwnerId();
    _isActive = true;
    _isPaused = false;
    if (!_isAllQueueActive) _abortRequested = false;
    if (!_isAllQueueActive) _stopAfterCurrent = false;
    if (!_isAllQueueActive) _stopKeepsNotStarted = false;
    if (!_isAllQueueActive) _runEndedKeepingNotStarted = false;
    if (!_isAllQueueActive) _leftRunJobIds = new Set();
    _activeBatchId = batchId;
    _activePhase = phase;
    _currentJobId = null;
    _activeCohortJobIds = new Set();
    _activeRunCompletion = new Promise(resolve => {
        _resolveActiveRunCompletion = resolve;
    });

    // Process-loop counters are initialised before any persisted pre-flight
    // work so the cleanup path can always emit an honest terminal event.
    let totalCompleted = 0;
    let totalFailed = 0;
    let totalSkipped = 0;
    // Set when a job steps back for a paused AI service; consumed at the top
    // of the next iteration, after that job's lease and claim are released.
    let providerWaitPending = null;
    const queueStartMs = Date.now();

    try {
        await _recoverExpiredProcessingLeases(phase);
        const requestedJobIds = Array.isArray(jobIds)
            ? new Set(jobIds.filter(id => id !== null && id !== undefined))
            : null;
        const pending = await db.batch_jobs
            .where('status').equals('pending')
            .and(r => (
                (r.phase || 'prepare-quote') === phase
                && (!requestedJobIds || requestedJobIds.has(r.id))
                && !_leftRunJobIds.has(r.id)
            ))
            .toArray();
        const approvedJobIds = new Set(pending.map(row => row.id));
        _activeCohortJobIds = new Set(approvedJobIds);
        if (_abortRequested) {
            await _cancelPendingJobIds(approvedJobIds, { phase, batchId });
        }

        const totalJobs = pending.length;
        _emit('qm-batch-queue-started', {
            batchId,
            phase,
            totalJobs,
            firstProjectId: pending.length > 0 ? pending[0].projectId : null,
        });

        // Re-fetch each iteration for cross-tab claim truth, but only from the
        // cohort frozen above. Later queue additions require a new confirmation.
        while (true) {
            if (_abortRequested) break;
            // Pause respects: spin-wait until resumed or aborted.
            // Practical impl: just await a tiny sleep loop. For tests, the
            // pause is checked between jobs, not between stages of a job.
            // Stop after this job also ends the wait: a pause pressed during
            // the job it was armed in must not hold the run here.
            while (_isPaused && !_abortRequested && !_stopAfterCurrent) {
                await _sleep(100);
            }
            if (_abortRequested) break;
            // Stop after this job: the job it was armed during has written its
            // own result. End the run here and cancel the jobs not started,
            // unless a run-wide fault armed it: those jobs stay pending.
            if (_stopAfterCurrent) {
                _stopAfterCurrent = false;
                if (_stopKeepsNotStarted) {
                    _endRunKeepingNotStarted();
                    break;
                }
                await abortQueue();
                break;
            }

            // A job just stepped back because the AI service is paused. Wait
            // here, between jobs, holding no lease and no server claim. Polled
            // so the operator's Stop ends the wait within a second.
            if (providerWaitPending) {
                const waitUntil = Date.now() + providerWaitPending.waitMs;
                providerWaitPending = null;
                while (Date.now() < waitUntil && !_queueAbortRequested(pipelineRunner)) {
                    await _sleep(Math.min(1000, Math.max(0, waitUntil - Date.now())));
                }
                if (_queueAbortRequested(pipelineRunner)) break;
                // Back to the top: a Pause pressed during the wait (up to five
                // minutes) must be honoured before the waiting job is claimed
                // and starts paid work.
                continue;
            }

            // A job put back on the run list since the run started waits
            // for the next run (_leftRunJobIds).
            for (const id of _leftRunJobIds) approvedJobIds.delete(id);
            // Claim the next pending row inside one IndexedDB transaction. A
            // second tab either claims a different row or receives null.
            const nextJob = await _claimNextPendingJob({
                targetDb: db,
                batchId,
                phase,
                ownerId,
                initialStage: stages[0],
                approvedJobIds,
            });
            if (!nextJob) break; // queue drained

            const jobStartMs = Date.now();
            // Set by Stage 5 when it kept the draft without the AI quote; null clears an earlier run's note.
            let aiQuoteNotWritten = null;
            // Set by the D2 runner when it handed the job to QuoteMaker after its summary (pipeline-runner.js).
            let handedToQuoteMaker = false;
            const uuidShape =/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const looksLikeFolderName = guardUnsyncedFolders && !uuidShape.test(String(nextJob.projectId || ''));
            let missingLocalProject = false;
            try {
                missingLocalProject = guardUnsyncedFolders
                    && !looksLikeFolderName
                    && !nextJob.deferredIntake
                    && db?.projects
                    && !(await db.projects.get(nextJob.projectId));
            } catch (error) {
                // The job was claimed before the local identity read. Release
                // that lease immediately on an infrastructure error so retrying
                // does not have to wait for expiry.
                await _persistOwnedUpdate(nextJob.id, ownerId, {
                    status: 'pending',
                    batchId: null,
                    startedAt: null,
                    currentStage: null,
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    stalledSince: null,
                });
                throw error;
            }
            if (looksLikeFolderName || missingLocalProject) {
                const errorCode = looksLikeFolderName ? 'UNSYNCED_FOLDER' : 'PROJECT_NOT_LOCAL';
                const errorMessage = looksLikeFolderName
                    ? `Unsynced folder "${nextJob.projectId}" - not yet imported. Open File Explorer, click the folder, then click Import. Once imported, re-queue it.`
                    : `Project ${nextJob.projectId} is referenced by the queue but is missing from this browser profile. Open File Explorer and queue its synced folder again so QuoteMaker can restore the project before processing.`;
                const errorDetails = { phase, stage: null, statusCode: null };
                const errorHistory = [
                    ...(Array.isArray(nextJob.errorHistory) ? nextJob.errorHistory : []),
                    { at: _nowIso(), message: errorMessage, code: errorCode, retryable: false, stage: null, details: errorDetails },
                ].slice(-10);
                const preflightFailureWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                    status: 'failed',
                    completedAt: _nowIso(),
                    currentStage: null,
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    stalledSince: null,
                    lastError: errorMessage,
                    lastErrorStage: null,
                    lastErrorCode: errorCode,
                    lastErrorRetryable: false,
                    lastErrorDetails: errorDetails,
                    errorHistory,
                }, pipelineRunner);
                if (preflightFailureWrite.aborted) {
                    await _persistOwnedUpdate(nextJob.id, ownerId, {
                        status: 'cancelled',
                        completedAt: _nowIso(),
                        currentStage: null,
                        claimOwnerId: null,
                        leaseHeartbeatAt: null,
                        leaseExpiresAt: null,
                    });
                    totalSkipped += 1;
                    break;
                }
                totalFailed += 1;
                _emit('qm-batch-job-failed', {
                    jobId: nextJob.id,
                    projectId: nextJob.projectId,
                    displayMeta: nextJob.displayMeta || null,
                    phase,
                    stage: null,
                    error: errorMessage,
                    errorCode,
                    retryable: false,
                    batchId,
                });
                _currentJobId = null;
                continue;
            }
            // The row is now identity-safe and ready to enter the pipeline.
            // Expose Skip only from this point; throwing a control sentinel
            // during the preflight branch would otherwise strand the lease.
            _currentJobId = nextJob.id;
            // BQ-CP-7: stamp pipeline-monitor with the batch context so every
            // entry produced during this job carries batchJobId/position/total.
            // Lazy-import so test environments without a window/PipelineMonitor
            // don't choke. Best-effort — telemetry never blocks the queue.
            //
            // v5.19.6 (autonomous test 2026-05-05): also call activate()
            // here as a defensive guarantee. App.jsx activates on mount
            // but HMR / late-mount edge cases sometimes leave PM inactive
            // when the queue starts — surfaces as "queue is running but
            // no sub-stage label appears in the drawer" because active=false
            // makes PipelineMonitor.start() a no-op (returns null).
            try {
                const { PipelineMonitor } = await import('./pipeline-monitor.js');
                PipelineMonitor.activate();
                PipelineMonitor.setBatchContext({
                    batchJobId: nextJob.id,
                    position: nextJob.position,
                    total: totalJobs,
                    batchId,
                });
            } catch { /* monitor not available — non-fatal */ }
            _emit('qm-batch-job-started', {
                jobId: nextJob.id,
                projectId: nextJob.projectId,
                displayMeta: nextJob.displayMeta || null,
                phase,
                position: nextJob.position,
                totalJobs,
                batchId,
            });
            if (typeof onProgress === 'function') {
                try { onProgress({ type: 'job-started', jobId: nextJob.id, projectId: nextJob.projectId, phase }); } catch {}
            }

            let leaseLost = false;
            let heartbeatInFlight = false;
            let quoteRunClaim = null;
            let quoteRunPinnedUpdatedAt = null;
            let quoteRunClaimOutcome = null;
            let quoteRunRenewalInFlight = null;
            let quoteRunHeartbeatInFlight = null;
            let quoteRunClaimLoss = null;
            let quoteRunReleaseAttempted = false;
            const persistQuoteRunAudit = async (patch) => {
                const current = await db.batch_jobs.get(nextJob.id);
                if (!current
                    || current.projectId !== nextJob.projectId
                    || current.batchId !== batchId
                    || (current.claimOwnerId && current.claimOwnerId !== ownerId)) {
                    return false;
                }
                await db.batch_jobs.update(nextJob.id, patch);
                return true;
            };
            const renewRemoteQuoteRunClaim = (expectedUpdatedAt = quoteRunPinnedUpdatedAt) => {
                if (!quoteRunClaim) {
                    const error = new Error(`Project ${nextJob.projectId} has no active Quote Run claim.`);
                    error.code = 'PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE';
                    error.category = 'project-coordination';
                    error.retryable = true;
                    throw error;
                }
                if (typeof expectedUpdatedAt !== 'string'
                    || !Number.isFinite(Date.parse(expectedUpdatedAt))) {
                    const error = new Error(`Project ${nextJob.projectId} has no reconciled Project version for Quote Run renewal.`);
                    error.code = 'PROJECT_QUOTE_RUN_VERSION_MISSING';
                    error.category = 'project-coordination';
                    error.retryable = true;
                    throw error;
                }
                if (quoteRunClaimLoss) throw quoteRunClaimLoss;
                const claimId = quoteRunClaim.claimId;
                if (quoteRunRenewalInFlight) {
                    // Join only a renewal of the same claim id. One sent before
                    // a take-back replaced the id answers for the old claim.
                    if (quoteRunRenewalInFlight.claimId === claimId
                        && Date.parse(quoteRunRenewalInFlight.expectedUpdatedAt)
                        === Date.parse(expectedUpdatedAt)) {
                        return quoteRunRenewalInFlight.promise;
                    }
                    // An earlier heartbeat may still be checking the Project
                    // version from before this boundary's reconciliation.
                    // Let it settle, then renew against the newly pinned
                    // version. Never let the stale promise satisfy this check.
                    return quoteRunRenewalInFlight.promise
                        .catch(() => {})
                        .then(() => renewRemoteQuoteRunClaim(expectedUpdatedAt));
                }
                const renewalRecord = { expectedUpdatedAt, claimId, promise: null };
                quoteRunRenewalInFlight = renewalRecord;
                renewalRecord.promise = (async () => {
                    try {
                        const receipt = await pipelineRunner.quoteRunClaims.renew(nextJob.projectId, {
                            claimId,
                            expectedUpdatedAt,
                            ttlSeconds: QUOTE_RUN_CLAIM_TTL_SECONDS,
                        });
                        if (quoteRunClaim?.claimId === claimId) quoteRunClaim = receipt;
                        return receipt;
                    } catch (error) {
                        // A heartbeat that fired while a take-back was in flight
                        // renewed the id the take-back replaced. The server
                        // refuses it as another session's run, but that says
                        // nothing about the claim this run now holds.
                        if (_isDefinitiveQuoteRunClaimLoss(error) && quoteRunClaim?.claimId === claimId) {
                            quoteRunClaimLoss = error;
                        }
                        throw error;
                    } finally {
                        if (quoteRunRenewalInFlight === renewalRecord) quoteRunRenewalInFlight = null;
                    }
                })();
                return renewalRecord.promise;
            };
            // Our own lease lapsed and nobody else took it: take it back rather
            // than failing a project that has already paid for earlier stages.
            // A session that DOES hold it answers PROJECT_QUOTE_RUN_OWNER_MISMATCH
            // or PROJECT_QUOTE_RUN_BUSY, both of which still stop this run.
            let lapsedClaimReacquires = 0;
            let quoteRunReacquireInFlight = null;
            const reacquireLapsedQuoteRunClaim = (expectedUpdatedAt) => {
                // The heartbeat and a stage boundary can both find the same
                // lapsed lease. Two acquires with different claim ids race on
                // the server, and the loser is refused as if another session
                // held the run. The second caller waits for the first, then
                // confirms the claim it took at its own version.
                if (quoteRunReacquireInFlight) {
                    return quoteRunReacquireInFlight
                        .catch(() => {})
                        .then(() => confirmRemoteQuoteRunClaim(expectedUpdatedAt));
                }
                const pending = (async () => {
                    // Bounded: a lease that keeps lapsing is a real coordination
                    // problem, not a throttled heartbeat, and must surface.
                    if (lapsedClaimReacquires >= _MAX_LAPSED_CLAIM_REACQUIRES) {
                        const error = new Error('This Quote Run keeps losing its claim on the project. It stopped before the next stage.');
                        error.code = 'PROJECT_QUOTE_RUN_CLAIM_NOT_ACTIVE';
                        error.category = 'project-coordination';
                        error.retryable = true;
                        throw error;
                    }
                    lapsedClaimReacquires += 1;
                    const claimId = _newBatchId();
                    const receipt = await pipelineRunner.quoteRunClaims.acquire(nextJob.projectId, {
                        claimId,
                        expectedUpdatedAt,
                        ttlSeconds: QUOTE_RUN_CLAIM_TTL_SECONDS,
                    });
                    quoteRunClaim = receipt;
                    quoteRunClaimLoss = null;
                    await persistQuoteRunAudit({
                        quoteRunClaimId: receipt.claimId,
                        quoteRunClaimedAt: _nowIso(),
                        quoteRunClaimExpiresAt: receipt.leaseExpiresAt,
                        quoteRunProjectUpdatedAt: receipt.projectUpdatedAt,
                        quoteRunClaimOutcome: null,
                        quoteRunClaimReleasedAt: null,
                    });
                    return receipt;
                })();
                quoteRunReacquireInFlight = pending;
                pending.finally(() => {
                    if (quoteRunReacquireInFlight === pending) quoteRunReacquireInFlight = null;
                }).catch(() => {});
                return pending;
            };
            // A claim confirmation refused as PROJECT_STALE_WRITE is tried once
            // more at the version this tab's own saves produced (see
            // _ownProjectVersion). Another device's edit still refuses it.
            const confirmRemoteQuoteRunClaim = async (expectedUpdatedAt = quoteRunPinnedUpdatedAt) => {
                // A take-back already under way owns the next claim id. The
                // server has granted it, so renewing the old id meanwhile is
                // refused as another session's run. Let it settle first.
                if (quoteRunReacquireInFlight) await quoteRunReacquireInFlight.catch(() => {});
                try {
                    return await confirmRemoteQuoteRunClaimAt(expectedUpdatedAt);
                } catch (error) {
                    if (!_isStaleProjectWrite(error)) throw error;
                    const ownVersion = _ownProjectVersion(pipelineRunner, nextJob.projectId);
                    if (!ownVersion || Date.parse(ownVersion) === Date.parse(expectedUpdatedAt)) throw error;
                    const receipt = await confirmRemoteQuoteRunClaimAt(ownVersion);
                    quoteRunPinnedUpdatedAt = ownVersion;
                    return receipt;
                }
            };
            const confirmRemoteQuoteRunClaimAt = async (expectedUpdatedAt) => {
                if (quoteRunClaimLoss && !_isLapsedOwnQuoteRunClaim(quoteRunClaimLoss)) throw quoteRunClaimLoss;
                if (quoteRunClaimLoss) {
                    quoteRunClaimLoss = null;
                    return reacquireLapsedQuoteRunClaim(expectedUpdatedAt);
                }
                try {
                    return await renewRemoteQuoteRunClaim(expectedUpdatedAt);
                } catch (firstError) {
                    if (_isLapsedOwnQuoteRunClaim(firstError)) {
                        quoteRunClaimLoss = null;
                        return reacquireLapsedQuoteRunClaim(expectedUpdatedAt);
                    }
                    if (quoteRunClaimLoss || _isAbortError(firstError)) throw firstError;
                    if (String(firstError?.code || firstError?.errorCode || '') === 'PROJECT_STALE_WRITE') {
                        throw firstError;
                    }
                    // A timer heartbeat can fail because connectivity flickered.
                    // Before any paid or terminal boundary, make one fresh,
                    // synchronous confirmation rather than treating the timer's
                    // uncertainty as either ownership loss or success.
                    try {
                        return await renewRemoteQuoteRunClaim(expectedUpdatedAt);
                    } catch (secondError) {
                        if (quoteRunClaimLoss || _isAbortError(secondError)) throw secondError;
                        if (String(secondError?.code || secondError?.errorCode || '') === 'PROJECT_STALE_WRITE') {
                            throw secondError;
                        }
                        throw _quoteRunClaimUncertain(secondError, nextJob.projectId);
                    }
                }
            };
            const reconcileAndConfirmRemoteQuoteRunClaim = async ({ heartbeatRecovery = false } = {}) => {
                if (heartbeatRecovery && quoteRunReleaseAttempted) return null;
                const reconciliation = await pipelineRunner.reconcileBackendProject(nextJob.projectId);
                // A background read may finish after STOP/failure released the
                // claim. It must not renew or stamp an already-terminal run.
                if (heartbeatRecovery && quoteRunReleaseAttempted) return null;
                const expectedUpdatedAt = _backendUpdatedAtFromReconciliation(
                    reconciliation,
                    nextJob.projectId,
                );
                quoteRunPinnedUpdatedAt = expectedUpdatedAt;
                const receipt = await confirmRemoteQuoteRunClaim(expectedUpdatedAt);
                if (heartbeatRecovery && quoteRunReleaseAttempted) return null;
                await persistQuoteRunAudit({
                    quoteRunClaimExpiresAt: receipt.leaseExpiresAt,
                    quoteRunProjectUpdatedAt: receipt.projectUpdatedAt,
                });
                return receipt;
            };
            const heartbeatRemoteQuoteRunClaim = () => {
                if (quoteRunHeartbeatInFlight) return quoteRunHeartbeatInFlight;
                const pending = (async () => {
                    try {
                        return await renewRemoteQuoteRunClaim();
                    } catch (error) {
                        if (quoteRunClaimLoss || quoteRunReleaseAttempted
                            || String(error?.code || error?.errorCode || '') !== 'PROJECT_STALE_WRITE') {
                            throw error;
                        }
                        // Summary and other awaited saves inside a long stage
                        // change the Project version. Renew first at the
                        // version this tab's own saves produced. The full
                        // reconciliation below refuses while the tab keeps
                        // saving, so on its own it let the lease lapse in the
                        // middle of Stage 1.
                        const ownVersion = _ownProjectVersion(pipelineRunner, nextJob.projectId);
                        if (ownVersion && Date.parse(ownVersion) !== Date.parse(quoteRunPinnedUpdatedAt)) {
                            try {
                                const receipt = await renewRemoteQuoteRunClaim(ownVersion);
                                if (quoteRunReleaseAttempted) return null;
                                quoteRunPinnedUpdatedAt = ownVersion;
                                await persistQuoteRunAudit({
                                    quoteRunClaimExpiresAt: receipt.leaseExpiresAt,
                                    quoteRunProjectUpdatedAt: receipt.projectUpdatedAt,
                                });
                                return receipt;
                            } catch (ownError) {
                                if (quoteRunClaimLoss || quoteRunReleaseAttempted || !_isStaleProjectWrite(ownError)) {
                                    throw ownError;
                                }
                            }
                        }
                        // Otherwise verify the full existing local/cloud
                        // reconciliation contract before renewing this same
                        // live owner; never trust a timestamp alone.
                        return reconcileAndConfirmRemoteQuoteRunClaim({ heartbeatRecovery: true });
                    }
                })();
                quoteRunHeartbeatInFlight = pending;
                pending.finally(() => {
                    if (quoteRunHeartbeatInFlight === pending) quoteRunHeartbeatInFlight = null;
                }).catch(() => {});
                return pending;
            };
            // Stall watchdog state. `lastProgressMs` advances only when the job
            // actually moves to a new stage — NOT when the lease is renewed —
            // so a heartbeat can no longer make a frozen job look alive.
            let lastProgressMs = Date.now();
            let stallReported = false;
            const noteProgress = () => {
                lastProgressMs = Date.now();
                if (stallReported) {
                    stallReported = false;
                    _emit('qm-batch-job-unstalled', {
                        jobId: nextJob.id, projectId: nextJob.projectId, phase, batchId,
                    });
                    // Best-effort: clear the durable marker so a recovered job
                    // stops rendering amber. Never let this fail the job.
                    _persistOwnedUpdate(nextJob.id, ownerId, { stalledSince: null }).catch(() => {});
                }
            };
            const heartbeat = globalThis.setInterval(async () => {
                if (heartbeatInFlight || leaseLost) return;
                heartbeatInFlight = true;
                try {
                    if (!(await _renewJobLease(nextJob.id, ownerId))) leaseLost = true;
                    // Report-only watchdog: say so, then keep going. Cancelling
                    // here would violate the no-timeouts-on-AI-pipelines rule.
                    const heartbeatNow = Date.now();
                    const stalledForMs = heartbeatNow - lastProgressMs;
                    if (_shouldReportStall({
                        lastProgressMs,
                        now: heartbeatNow,
                        alreadyReported: stallReported,
                        leaseLost,
                    })) {
                        stallReported = true;
                        const stalledSince = new Date(lastProgressMs).toISOString();
                        try {
                            await _persistOwnedUpdate(nextJob.id, ownerId, { stalledSince });
                        } catch { /* marker is best-effort; the event still fires */ }
                        _emit('qm-batch-job-stalled', {
                            jobId: nextJob.id,
                            projectId: nextJob.projectId,
                            phase,
                            batchId,
                            stage: nextJob.currentStage ?? null,
                            stalledSince,
                            stalledForMs,
                        });
                    }
                } catch {
                    // A transient IndexedDB failure must stop this runner from
                    // claiming success after its durable ownership is unknown.
                    leaseLost = true;
                } finally {
                    heartbeatInFlight = false;
                }
                if (quoteRunClaim && !quoteRunClaimLoss) {
                    // Do not await network coordination inside the local
                    // IndexedDB heartbeat. One slow remote request must not
                    // prevent this tab renewing its 90-second local lease.
                    void heartbeatRemoteQuoteRunClaim().catch(() => {
                        // Remote ownership loss is persisted as this Project's
                        // normal failure at the next paid/terminal boundary.
                        // Transient uncertainty is synchronously retried there.
                    });
                }
            }, JOB_LEASE_HEARTBEAT_MS);
            const assertLease = async () => {
                if (leaseLost) {
                    throw new _LeaseLostError(`Queue lease for job ${nextJob.id} was lost while processing`);
                }
                try {
                    if (!(await _renewJobLease(nextJob.id, ownerId))) leaseLost = true;
                } catch {
                    leaseLost = true;
                }
                if (leaseLost) {
                    throw new _LeaseLostError(`Queue lease for job ${nextJob.id} was lost while processing`);
                }
            };
            const persistCancellation = async (elapsedMs) => {
                quoteRunClaimOutcome = quoteRunClaim ? 'cancelled' : quoteRunClaimOutcome;
                // The write below clears currentStage, so keep the stage the
                // job had reached. A failed read only loses that detail.
                let stoppedAtStage = null;
                try {
                    stoppedAtStage = (await db.batch_jobs.get(nextJob.id))?.currentStage ?? null;
                } catch { /* the cancellation still writes */ }
                return _persistOwnedUpdate(nextJob.id, ownerId, {
                    status: 'cancelled',
                    // Reached only after the queue's own stop was requested.
                    // A cancelled row must always say why.
                    cancelReason: 'run-stopped-while-running',
                    stoppedAtStage,
                    completedAt: _nowIso(),
                    currentStage: null,
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    elapsedMs,
                });
            };

            try {
                // Drive the project through the runner's stages
                _throwIfQueueAborted(pipelineRunner);
                _throwIfSkipRequested(nextJob.id);
                let selectedFileIdsForRun = nextJob.selectedFileIds || null;
                if (phase === 'pre-processing' && nextJob.deferredIntake) {
                    if (typeof pipelineRunner.materializeProject !== 'function') {
                        const error = new Error('The pre-processing runner cannot read this deferred project source.');
                        error.code = 'DEFERRED_INTAKE_MATERIALIZER_UNAVAILABLE';
                        error.retryable = true;
                        throw error;
                    }
                    const leaseNow = Date.now();
                    await _persistOwnedUpdate(nextJob.id, ownerId, {
                        currentStage: 'materialize-files',
                        materializationState: 'reading',
                        materializationStartedAt: _nowIso(),
                        // This attempt began a stage of its own (retryFailed
                        // reads it against the page-closed stamp).
                        stageStartedAt: _nowIso(),
                        leaseHeartbeatAt: new Date(leaseNow).toISOString(),
                        leaseExpiresAt: _leaseExpiryIso(leaseNow),
                    });
                    noteProgress();
                    _emit('qm-batch-stage-advance', {
                        jobId: nextJob.id,
                        projectId: nextJob.projectId,
                        phase,
                        stage: 'materialize-files',
                        batchId,
                    });
                    const materialized = await pipelineRunner.materializeProject(nextJob.projectId, {
                        jobId: nextJob.id,
                        claimOwnerId: ownerId,
                        deferredIntake: nextJob.deferredIntake,
                        selectedFileIds: selectedFileIdsForRun,
                        abortSignal: pipelineRunner.abortSignal || null,
                    });
                    if (Array.isArray(materialized?.selectedFileIds)) {
                        selectedFileIdsForRun = materialized.selectedFileIds;
                    } else if (materialized?.selectedFileIds === null) {
                        selectedFileIdsForRun = null;
                    }
                    if (materialized?.displayMeta && typeof materialized.displayMeta === 'object') {
                        // Materialisation replaces the intake snapshot with the
                        // durable project identity. Keep this claimed row in
                        // step so same-run completion/failure events do not
                        // announce the stale source-folder label.
                        nextJob.displayMeta = materialized.displayMeta;
                    }
                    await assertLease();
                    _throwIfQueueAborted(pipelineRunner, 'Queue aborted while reading project files');
                    _throwIfSkipRequested(nextJob.id);
                }
                // A prepare runner may expose a backend reconciliation hook.
                // It runs before loadProject because load can itself perform
                // AI-backed RFQ identity extraction. Keeping the hook here also
                // means every network request completes outside the queue's
                // short IndexedDB state-transition transactions.
                if (phase === 'prepare-quote'
                    && typeof pipelineRunner.reconcileBackendProject === 'function') {
                    const reconciliation = await pipelineRunner.reconcileBackendProject(nextJob.projectId);
                    await assertLease();
                    _throwIfQueueAborted(pipelineRunner);
                    _throwIfSkipRequested(nextJob.id);
                    const expectedUpdatedAt = _backendUpdatedAtFromReconciliation(
                        reconciliation,
                        nextJob.projectId,
                    );
                    const claimId = _newBatchId();
                    quoteRunClaim = await pipelineRunner.quoteRunClaims.acquire(nextJob.projectId, {
                        claimId,
                        expectedUpdatedAt,
                        ttlSeconds: QUOTE_RUN_CLAIM_TTL_SECONDS,
                    });
                    quoteRunPinnedUpdatedAt = quoteRunClaim.projectUpdatedAt;
                    await _persistOwnedUpdate(nextJob.id, ownerId, {
                        quoteRunClaimId: quoteRunClaim.claimId,
                        quoteRunClaimedAt: _nowIso(),
                        quoteRunClaimExpiresAt: quoteRunClaim.leaseExpiresAt,
                        quoteRunProjectUpdatedAt: quoteRunClaim.projectUpdatedAt,
                        quoteRunClaimOutcome: null,
                        quoteRunClaimReleasedAt: null,
                        quoteRunClaimReleaseWarning: null,
                    });
                    await assertLease();
                    _throwIfQueueAborted(pipelineRunner);
                    _throwIfSkipRequested(nextJob.id);
                }
                await pipelineRunner.loadProject(nextJob.projectId, {
                    selectedFileIds: selectedFileIdsForRun,
                });
                await assertLease();
                _throwIfQueueAborted(pipelineRunner);
                _throwIfSkipRequested(nextJob.id);
                for (const stage of stages) {
                    _throwIfQueueAborted(pipelineRunner, 'Queue aborted mid-stage');
                    _throwIfSkipRequested(nextJob.id);
                    const leaseNow = Date.now();
                    await _persistOwnedUpdate(nextJob.id, ownerId, {
                        currentStage: stage,
                        // This attempt began a stage of its own (retryFailed
                        // reads it against the page-closed stamp).
                        stageStartedAt: _nowIso(),
                        leaseHeartbeatAt: new Date(leaseNow).toISOString(),
                        leaseExpiresAt: _leaseExpiryIso(leaseNow),
                    });
                    _throwIfSkipRequested(nextJob.id);
                    // Real forward motion — the only thing that resets the
                    // stall watchdog. Lease renewal deliberately does not.
                    noteProgress();
                    _emit('qm-batch-stage-advance', {
                        jobId: nextJob.id,
                        projectId: nextJob.projectId,
                        phase,
                        stage,
                        batchId,
                    });
                    const fn = pipelineRunner[_stageFunctionName(stage)];
                    // Reconcile after the queue bookkeeping and immediately
                    // before every paid stage. The first reconciliation proves
                    // the load boundary; these checks prevent a cloud edit made
                    // during a long queued run from being ignored by the next
                    // AI invocation.
                    if (phase === 'prepare-quote'
                        && typeof pipelineRunner.reconcileBackendProject === 'function') {
                        await reconcileAndConfirmRemoteQuoteRunClaim();
                        await assertLease();
                        _throwIfQueueAborted(pipelineRunner, 'Queue aborted before paid stage');
                        _throwIfSkipRequested(nextJob.id);
                    }
                    // v5.37.24 Phase C: pass the job's per-project scope
                    // filter through as the second argument. Runners that
                    // honour it (today: preprocessing-runner.js) will limit
                    // their work to selectedFileIds. Runners that don't care
                    // (Stage 1..5 prepare-quote runners) ignore the extra arg
                    // — JS function args are duck-typed.
                    const stageOutcome = await fn(nextJob.projectId, {
                        selectedFileIds: selectedFileIdsForRun,
                        // Every prepare-quote entry point must prove the exact
                        // UUID was registered by deterministic intake (or was
                        // fetched authoritatively) before any AI call fires.
                        requireBackendVerification: phase === 'prepare-quote',
                    });
                    // Stage 5 says when it kept the draft without the AI quote.
                    if (typeof stageOutcome?.aiQuoteNotWritten === 'string' && stageOutcome.aiQuoteNotWritten.trim()) {
                        aiQuoteNotWritten = stageOutcome.aiQuoteNotWritten.trim();
                    }
                    if (stageOutcome?.handedToQuoteMaker === true) handedToQuoteMaker = true;
                    await assertLease();
                    _throwIfQueueAborted(pipelineRunner, 'Queue aborted mid-stage');
                    _throwIfSkipRequested(nextJob.id);
                }
                // Post-final-stage: mark terminal state per phase.
                _throwIfQueueAborted(pipelineRunner, 'Queue aborted before terminal write');
                _throwIfSkipRequested(nextJob.id);
                if (quoteRunClaim) await reconcileAndConfirmRemoteQuoteRunClaim();
                await pipelineRunner[terminalFnName](nextJob.projectId);
                await assertLease();
                _throwIfQueueAborted(pipelineRunner, 'Queue aborted before project save');
                _throwIfSkipRequested(nextJob.id);
                if (quoteRunClaim) await reconcileAndConfirmRemoteQuoteRunClaim();
                await pipelineRunner.saveProject(nextJob.projectId);
                await assertLease();
                _throwIfQueueAborted(pipelineRunner, 'Queue aborted before completion write');
                _throwIfSkipRequested(nextJob.id);
                if (quoteRunClaim) await reconcileAndConfirmRemoteQuoteRunClaim();

                const elapsed = Date.now() - jobStartMs;
                // Intake completions carry the deferred-media record onto the
                // queue row so the drawer can SHOW what was set aside (a file
                // gap must never be a silent exclusion — DEC-BQ-030).
                let deferredMedia = null;
                if (phase === 'pre-processing') {
                    try {
                        const finishedProject = await db.projects.get(nextJob.projectId);
                        const deferred = finishedProject?.intakeDeferredMedia;
                        // Always write the array, including empty: a re-run that
                        // RESOLVED the deferral must clear the row, or the
                        // drawer keeps announcing files that are now fine.
                        deferredMedia = Array.isArray(deferred) ? deferred : [];
                    } catch { /* best-effort — the project row is the source of truth */ }
                } else if (phase === 'prepare-quote') {
                    deferredMedia = await _quoteRunSetAsideMedia(nextJob);
                }
                const completionWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                    status: 'completed',
                    completedAt: _nowIso(),
                    currentStage: null,
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    stalledSince: null,
                    // This attempt ran to the end, so an earlier page-closed
                    // stamp no longer describes the job.
                    interruptedAt: null,
                    interruptedAtStage: null,
                    interruptedOwnerId: null,
                    elapsedMs: elapsed,
                    ...(deferredMedia !== null ? { deferredMedia } : {}),
                    // The run kept its Stage 5 draft without the AI quote
                    // (Peter, 2026-10-23: carry on and flag as Full AI does).
                    ...(phase === 'prepare-quote' ? { aiQuoteNotWritten } : {}),
                    // QuoteMaker writes the quote on the server. The stored outcome is the one v6.0.135 wrote for a
                    // hand-off, so its rows and these read alike (quote-run-view.ts).
                    ...(phase === 'prepare-quote' ? { completionOutcome: handedToQuoteMaker ? 'quote_brain_handoff' : null } : {}),
                }, pipelineRunner);
                if (completionWrite.aborted) {
                    throw new _CancelledError('Queue aborted before completion write');
                }
                quoteRunClaimOutcome = quoteRunClaim ? 'complete' : quoteRunClaimOutcome;
                totalCompleted += 1;
                _emit('qm-batch-job-completed', {
                    jobId: nextJob.id,
                    projectId: nextJob.projectId,
                    selectedFileIds: nextJob.selectedFileIds || null,
                    displayMeta: nextJob.displayMeta || null,
                    phase,
                    batchId,
                    elapsedMs: elapsed,
                    ...(phase === 'prepare-quote' && handedToQuoteMaker ? { outcome: 'quote_brain_handoff' } : {}),
                });
            } catch (err) {
                const elapsed = Date.now() - jobStartMs;
                if (err instanceof _LeaseLostError) throw err;
                // Only the queue's own abort state proves an operator Stop. Every
                // genuine stop path (Quote Run sheet, QueueDrawer, QM Assistant,
                // STOP-AI pill, session teardown) aborts the runner controller
                // or sets _abortRequested BEFORE the awaited stage rejects.
                //
                // An error that is merely NAMED AbortError is not proof. The
                // app's stale-run guard (PROJECT_RUN_STALE) carries that name,
                // and so does any fetch bound to a project signal that a reload
                // replaced. Treating those as a Stop wrote 'cancelled' with no
                // error, then broke out of the loop: one project vanished from
                // the run list without a reason and every later project never
                // started. That was the production signature for Quote Run
                // never passing Stage 1 (2026-09-20). Such an error is a real
                // failure of THIS project: record it with its code so the
                // operator can see and retry it, and carry on with the rest.
                const cancelled = err instanceof _CancelledError
                    || _queueAbortRequested(pipelineRunner);
                if (!cancelled && _isAbortError(err)) {
                    console.warn(
                        `[batch-queue] Job ${nextJob.id} stopped by a non-operator abort`
                        + ` (${err?.code || 'no code'}): ${err?.message || err}`,
                    );
                }
                // An awaited runner operation can reject at the same moment the
                // operator requests Skip. The request is bound to this job, so
                // consume it here instead of misclassifying the target as a
                // pipeline failure (or leaking the request to a later job).
                // Stop/Abort has precedence over Skip because it applies to the
                // whole active run and must persist as cancellation.
                if (!cancelled && _consumeSkipRequest(nextJob.id)) {
                    err = new _SkippedError('Queue job skipped by operator');
                }
                if (cancelled) {
                    // Cancelled — mark cancelled and break out of loop
                    await persistCancellation(elapsed);
                    totalSkipped += 1;
                    break;
                }
                if (err instanceof _SkippedError) {
                    quoteRunClaimOutcome = quoteRunClaim ? 'cancelled' : quoteRunClaimOutcome;
                    const skippedWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                        status: 'skipped',
                        completedAt: _nowIso(),
                        currentStage: null,
                        claimOwnerId: null,
                        leaseHeartbeatAt: null,
                        leaseExpiresAt: null,
                        elapsedMs: elapsed,
                    }, pipelineRunner);
                    if (skippedWrite.aborted) {
                        await persistCancellation(elapsed);
                        totalSkipped += 1;
                        break;
                    }
                    totalSkipped += 1;
                    continue;
                }
                // A deterministic Stage 4 readiness block is an expected,
                // successful human handoff: the runner has already persisted
                // the project as a truthful Stage 4 draft. Complete this queue
                // row as needs-review without invoking the Stage 5 terminal
                // writer or inflating technical-failure totals. Persistence
                // failures use QUOTE_REVIEW_STATE_PERSIST_FAILED and continue
                // through the real-error branch below.
                const stageOnFail = (await db.batch_jobs.get(nextJob.id))?.currentStage || null;
                const failure = _normaliseBatchError(err, { phase, stage: stageOnFail });
                const currentJob = await db.batch_jobs.get(nextJob.id);
                if (phase === 'prepare-quote' && failure.code === 'QUOTE_REVIEW_REQUIRED') {
                    quoteRunClaimOutcome = quoteRunClaim ? 'review-required' : quoteRunClaimOutcome;
                    const reviewedAt = _nowIso();
                    const reviewHistory = [
                        ...(Array.isArray(currentJob?.reviewHistory) ? currentJob.reviewHistory : []),
                        {
                            at: reviewedAt,
                            message: failure.message,
                            code: failure.code,
                            stage: failure.details?.reviewStage ?? stageOnFail,
                            details: failure.details,
                        },
                    ].slice(-10);
                    const reviewSetAside = await _quoteRunSetAsideMedia(nextJob);
                    const reviewWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                        status: 'completed',
                        completedAt: reviewedAt,
                        ...(reviewSetAside !== null ? { deferredMedia: reviewSetAside } : {}),
                        currentStage: null,
                        claimOwnerId: null,
                        leaseHeartbeatAt: null,
                        leaseExpiresAt: null,
                        completionOutcome: 'stage4_review_required',
                        reviewCode: failure.code,
                        reviewMessage: failure.message,
                        reviewStage: failure.details?.reviewStage ?? 4,
                        reviewDetails: failure.details,
                        reviewHistory,
                        lastError: null,
                        lastErrorStage: null,
                        lastErrorCode: null,
                        lastErrorRetryable: null,
                        lastErrorDetails: null,
                        // The handoff ends an attempt that ran its stages, so
                        // an earlier page-closed stamp no longer describes it.
                        interruptedAt: null,
                        interruptedAtStage: null,
                        interruptedOwnerId: null,
                        elapsedMs: elapsed,
                    }, pipelineRunner);
                    if (reviewWrite.aborted) {
                        await persistCancellation(elapsed);
                        totalSkipped += 1;
                        break;
                    }
                    totalCompleted += 1;
                    _emit('qm-batch-job-completed', {
                        jobId: nextJob.id,
                        projectId: nextJob.projectId,
                        selectedFileIds: nextJob.selectedFileIds || null,
                        displayMeta: nextJob.displayMeta || null,
                        phase,
                        batchId,
                        elapsedMs: elapsed,
                        outcome: 'stage4_review_required',
                        reviewCode: failure.code,
                        reviewStage: failure.details?.reviewStage ?? 4,
                    });
                    continue;
                }

                // The AI service is paused, not this project. The gateway
                // quarantines a rate-limited key for 60s and answers 503
                // all_keys_quarantined with a retryAfterSeconds hint. Failing
                // the project here would discard its finished steps' momentum
                // and hand the run straight to the next project, which meets
                // the same paused keys seconds later: one rate limit turned an
                // entire run red. Wait out the cooldown and try this same job
                // again. Completed steps hold durable receipts, so the retry
                // resumes rather than repeats. Stop still ends the wait.
                const providerWaitMs = _transientProviderWaitMs(err, failure, currentJob, batchId);
                if (providerWaitMs > 0) {
                    quoteRunClaimOutcome = quoteRunClaim ? 'failed' : quoteRunClaimOutcome;
                    const providerRetryCount = _providerRetriesThisRun(currentJob, batchId) + 1;
                    const resumeAt = new Date(Date.now() + providerWaitMs).toISOString();
                    // An attempt that took its own server claim (or makes
                    // none) has moved past an earlier page-closed stamp. One
                    // that waits before its claim ran no stage, and the closed
                    // page's claim may still hold the project: the stamp stays,
                    // so a busy refusal on the retry still reads as that page
                    // (import-workflow closedPageClaimHold).
                    const makesServerClaim = phase === 'prepare-quote'
                        && typeof pipelineRunner.reconcileBackendProject === 'function';
                    const passedOwnClaim = quoteRunClaim != null || !makesServerClaim;
                    const waitWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                        status: 'pending',
                        currentStage: null,
                        claimOwnerId: null,
                        leaseHeartbeatAt: null,
                        leaseExpiresAt: null,
                        stalledSince: null,
                        providerRetryCount,
                        providerRetryBatchId: batchId,
                        providerWaitUntil: resumeAt,
                        ...(passedOwnClaim
                            ? { interruptedAt: null, interruptedAtStage: null, interruptedOwnerId: null }
                            : {}),
                        lastError: failure.message,
                        lastErrorStage: stageOnFail,
                        lastErrorCode: failure.code,
                        lastErrorRetryable: true,
                        lastErrorDetails: failure.details,
                        errorHistory: [
                            ...(Array.isArray(currentJob?.errorHistory) ? currentJob.errorHistory : []),
                            {
                                at: _nowIso(),
                                message: `${failure.message} Waiting ${Math.round(providerWaitMs / 1000)}s, then retrying automatically (attempt ${providerRetryCount} of ${_MAX_PROVIDER_RETRIES}).`,
                                code: failure.code,
                                retryable: true,
                                stage: stageOnFail,
                                details: failure.details,
                            },
                        ].slice(-10),
                        elapsedMs: elapsed,
                    }, pipelineRunner);
                    if (waitWrite.aborted) {
                        await persistCancellation(elapsed);
                        totalSkipped += 1;
                        break;
                    }
                    _emit('qm-batch-job-provider-wait', {
                        jobId: nextJob.id,
                        projectId: nextJob.projectId,
                        displayMeta: nextJob.displayMeta || null,
                        phase,
                        stage: stageOnFail,
                        errorCode: failure.code,
                        waitMs: providerWaitMs,
                        resumeAt,
                        attempt: providerRetryCount,
                        batchId,
                    });
                    providerWaitPending = { jobId: nextJob.id, waitMs: providerWaitMs };
                    continue;
                }

                // Real error — record + emit failed; continue queue if skipOnFailure
                quoteRunClaimOutcome = quoteRunClaim ? 'failed' : quoteRunClaimOutcome;
                const errorHistory = [
                    ...(Array.isArray(currentJob?.errorHistory) ? currentJob.errorHistory : []),
                    {
                        at: _nowIso(),
                        message: failure.message,
                        code: failure.code,
                        retryable: failure.retryable,
                        stage: stageOnFail,
                        details: failure.details,
                    },
                ].slice(-10);
                const failureWrite = await _persistOwnedUpdateUnlessAborted(nextJob.id, ownerId, {
                    status: 'failed',
                    completedAt: _nowIso(),
                    currentStage: null,
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    stalledSince: null,
                    lastError: failure.message,
                    lastErrorStage: stageOnFail,
                    lastErrorCode: failure.code,
                    lastErrorRetryable: failure.retryable,
                    lastErrorDetails: failure.details,
                    errorHistory,
                    elapsedMs: elapsed,
                }, pipelineRunner);
                if (failureWrite.aborted) {
                    await persistCancellation(elapsed);
                    totalSkipped += 1;
                    break;
                }
                totalFailed += 1;
                _emit('qm-batch-job-failed', {
                    jobId: nextJob.id,
                    projectId: nextJob.projectId,
                    displayMeta: nextJob.displayMeta || null,
                    phase,
                    stage: stageOnFail,
                    error: failure.message,
                    errorCode: failure.code,
                    retryable: failure.retryable,
                    errorDetails: failure.details,
                    batchId,
                });
                if (!skipOnFailure) break;
            } finally {
                globalThis.clearInterval(heartbeat);
                if (quoteRunClaim && !quoteRunReleaseAttempted) {
                    quoteRunReleaseAttempted = true;
                    const releaseOutcome = quoteRunClaimOutcome
                        || (_queueAbortRequested(pipelineRunner) ? 'cancelled' : 'failed');
                    try {
                        const releaseReceipt = await pipelineRunner.quoteRunClaims.release(nextJob.projectId, {
                            claimId: quoteRunClaim.claimId,
                            outcome: releaseOutcome,
                        });
                        await persistQuoteRunAudit({
                            quoteRunClaimExpiresAt: null,
                            quoteRunClaimOutcome: releaseOutcome,
                            quoteRunClaimReleasedAt: releaseReceipt.releasedAt,
                            quoteRunClaimReleaseWarning: null,
                        });
                    } catch (releaseError) {
                        // The claim is still held on the server until its lease
                        // runs out. The automatic retry acquires with a NEW
                        // claim id, so a shorter wait would be refused by this
                        // run's own leftover claim (PROJECT_QUOTE_RUN_BUSY,
                        // shown as "running in another session"). The failures
                        // that trigger a wait (502/503, a dropped connection)
                        // are the same ones that make this release fail.
                        if (providerWaitPending?.jobId === nextJob.id) {
                            providerWaitPending = {
                                ...providerWaitPending,
                                waitMs: Math.max(
                                    providerWaitPending.waitMs,
                                    (QUOTE_RUN_CLAIM_TTL_SECONDS * 1000) + 5000,
                                ),
                            };
                        }
                        // Releasing coordination metadata is important, but it
                        // is not quote generation. A failed release must never
                        // turn a persisted draft into a failed/regenerated job.
                        try {
                            await persistQuoteRunAudit({
                                quoteRunClaimOutcome: releaseOutcome,
                                quoteRunClaimReleaseWarning: _quoteRunReleaseWarning(
                                    releaseError,
                                    releaseOutcome,
                                ),
                            });
                        } catch { /* warning metadata is best-effort */ }
                    }
                }
                if (_skipRequestedJobId === nextJob.id) _skipRequestedJobId = null;
                _currentJobId = null;
                // BQ-CP-7: clear batch context after each job so the next
                // job's start() call repopulates with its own metadata.
                try {
                    const { PipelineMonitor } = await import('./pipeline-monitor.js');
                    PipelineMonitor.setBatchContext(null);
                } catch { /* non-fatal */ }
            }
        }
        // Either form of stop (the module flag or the runner's own signal) means
        // rows were left pending on purpose. A job waiting out a paused AI
        // service is 'pending' and uncounted, so a Stop during that wait must
        // not be mistaken for a run that claimed nothing.
        if (!_queueAbortRequested(pipelineRunner)
            && totalJobs > 0
            && totalCompleted + totalFailed + totalSkipped === 0) {
            const stillPending = await db.batch_jobs
                .where('status').equals('pending')
                .and(row => (row.phase || 'prepare-quote') === phase)
                .toArray();
            if (stillPending.length > 0) {
                throw new Error(
                    `startQueue: observed ${totalJobs} pending job(s) but claimed none; ` +
                    `${stillPending.length} remain pending instead of reporting a false drain`
                );
            }
        }
    } finally {
        // Counted while this run still holds the queue, so no other run can
        // start in between. None kept (the fault met the last job) is an
        // ordinary end: every job ran.
        const notStarted = _runEndedKeepingNotStarted ? await _countKeptNotStarted() : 0;
        _isActive = false;
        _isPaused = false;
        const elapsed = Date.now() - queueStartMs;
        _emit('qm-batch-queue-drained', {
            batchId,
            phase,
            totalCompleted,
            totalFailed,
            totalSkipped,
            elapsedMs: elapsed,
            ...(notStarted > 0 ? { keptNotStarted: true, notStarted } : {}),
        });
        _activeBatchId = null;
        _activePhase = null;
        _currentJobId = null;
        _activeCohortJobIds = new Set();
        // A run of this phase alone is over: getQueueStatus() now says idle.
        // Inside a prepare-all run it is not (startAllQueuedProjects raises
        // this itself once the whole run ends).
        if (!_isAllQueueActive) _emit('qm-batch-queue-idle', { batchId, phase });
        const resolveRunCompletion = _resolveActiveRunCompletion;
        _resolveActiveRunCompletion = null;
        resolveRunCompletion?.();
    }
}

// Map a stage identifier (number or string) to the runner function name.
// Stage 1 → 'runStage1'; 'preprocess' → 'runStagePreprocess'; 'risk' → 'runStageRisk'.
function _stageFunctionName(stage) {
    const raw = String(stage);
    // Numeric stages (1..5) keep the historical name (runStage1..runStage5).
    if (/^\d+$/.test(raw)) return `runStage${raw}`;
    // Word stages (kebab/snake → CamelCase suffix).
    const camel = raw
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(seg => seg.charAt(0).toUpperCase() + seg.slice(1))
        .join('');
    return `runStage${camel}`;
}

/**
 * Convenience wrapper for the prepare-quote (Stage 1→5 AI cascade) phase.
 * Existing call sites that called startQueue({ pipelineRunner }) still work
 * because phase defaults to 'prepare-quote' — this wrapper just makes the
 * intent explicit.
 */
export async function startPrepareQuoteQueue(opts = {}) {
    return startQueue({ ...opts, phase: 'prepare-quote' });
}

/**
 * Convenience wrapper for the pre-processing (intake) phase. The runner
 * passed here exposes `stages: ['preprocess']`, `runStagePreprocess`, and
 * `markReadyForAnalysis` (see src/services/preprocessing-runner.js).
 */
export async function startPreProcessingQueue(opts = {}) {
    return startQueue({ ...opts, phase: 'pre-processing' });
}

function projectHasDurableIntakeReadyProof(project, projectId) {
    const backendDisabled = import.meta.env?.VITE_USE_BACKEND === 'false'
        || import.meta.env?.VITE_USE_BACKEND === false;
    return hasDurableIntakeReadyProof(project, projectId, { backendDisabled });
}

async function findPromotableCompletedIntakes(rows, targetDb = db) {
    rows = rows.filter(row => !row.dismissedAt);
    const prepareProjectIds = new Set(
        rows
            .filter(row => (row.phase || 'prepare-quote') === 'prepare-quote')
            .map(row => row.projectId),
    );
    const candidates = rows.filter(row => (
        (row.phase || 'prepare-quote') === 'pre-processing'
        && row.status === 'completed'
        && !isProjectDeletionTombstone(row)
        && !prepareProjectIds.has(row.projectId)
    ));
    const promotable = [];
    for (const row of candidates) {
        const project = await targetDb.projects.get(row.projectId);
        if (projectHasDurableIntakeReadyProof(project, row.projectId)) promotable.push(row);
    }
    return promotable;
}

/**
 * @returns {Promise<{ healed: number, retired: number }>} healed rows went back
 * to pending for a fresh intake run; retired rows belonged to projects that
 * advanced beyond intake outside the queue and became skipped audit history.
 * Both are reported so the mount surface can explain either change.
 */
async function reconcileUnpromotableCompletedIntakes(targetDb = db) {
    if (!targetDb?.projects || !targetDb?.batch_jobs) return { healed: 0, retired: 0 };
    return _withTransaction(targetDb, [targetDb.projects, targetDb.batch_jobs], async () => {
        const rows = (await targetDb.batch_jobs.toArray()).filter(row => !row.dismissedAt);
        const prepareProjectIds = new Set(
            rows
                .filter(row => (row.phase || 'prepare-quote') === 'prepare-quote')
                .map(row => row.projectId),
        );
        const candidates = rows.filter(row => (
            (row.phase || 'prepare-quote') === 'pre-processing'
            && row.status === 'completed'
            && !isProjectDeletionTombstone(row)
            && !prepareProjectIds.has(row.projectId)
        ));
        let reconciled = 0;
        let retired = 0;
        for (const row of candidates) {
            const project = await targetDb.projects.get(row.projectId);
            if (projectHasDurableIntakeReadyProof(project, row.projectId)) continue;
            const reconciledAt = _nowIso();
            // 2026-07-29 (batch-quoting-first-run): self-heal instead of
            // stranding — but ONLY for projects still in intake shape. A
            // project that advanced beyond intake outside the queue (draft,
            // quoted, won, …) voids the proof by DESIGN; its intake row is
            // satisfied history, and re-running intake over it would mutate a
            // live record and then die on the backend-newer guard. Those rows
            // retire as 'skipped' audit history instead (2026-07-29 review
            // finding). Intake-shaped or locally-missing projects go back to
            // pending: intake is deterministic and idempotent, and a genuine
            // failure (backend down, source gone) then records its REAL error
            // on the next run.
            // Use the SAME predicate as the backend-newer guard that would
            // reject the re-run, rather than a hand-rolled status list: it
            // checks generated artefacts first (a project still labelled
            // ready_for_analysis but carrying a risk analysis is NOT intake
            // shaped) and then the canonical intake-only status set (which
            // includes 'draft' — every intake status collapses to draft on a
            // backend round-trip).
            const intakeShaped = !project || !projectHasGeneratedWork(project);
            const message = intakeShaped
                ? 'Completed intake was missing durable verification — intake re-queued automatically.'
                : 'Project advanced beyond intake outside the queue — intake history retired.';
            const details = {
                phase: 'pre-processing',
                previousStatus: 'completed',
                previousCompletedAt: row.completedAt || null,
                projectStatus: project?.status || null,
                backendVerificationMode: project?.backendVerificationMode || null,
            };
            const errorHistory = [
                ...(Array.isArray(row.errorHistory) ? row.errorHistory : []),
                {
                    at: reconciledAt,
                    message,
                    code: intakeShaped ? 'INTAKE_READY_PROOF_MISSING' : 'INTAKE_HISTORY_RETIRED',
                    retryable: intakeShaped,
                    stage: null,
                    details,
                },
            ].slice(-10);
            await targetDb.batch_jobs.update(row.id, intakeShaped ? {
                status: 'pending',
                // The unproven completion is void — that is why the row re-runs
                // — but the original completion instant stays on the audit trail.
                completedAt: null,
                intakeCompletedAt: row.intakeCompletedAt || row.completedAt || null,
                reconciliationAt: reconciledAt,
                // Align with the expired-lease recovery convention: a healed
                // row belongs to no batch until a run claims it.
                batchId: null,
                currentStage: null,
                lastError: null,
                lastErrorStage: null,
                lastErrorCode: null,
                lastErrorRetryable: null,
                lastErrorDetails: null,
                // The row waits again because the proof is missing, not
                // because a page closed: its last attempt finished. Rows
                // finished before the completion write cleared the stamp
                // still carry it, so it goes here too.
                interruptedAt: null,
                interruptedAtStage: null,
                interruptedOwnerId: null,
                errorHistory,
            } : {
                // 'skipped' is immutable queue audit (requeueable history):
                // the drawer stops presenting the project as queue-owned, and
                // an explicit re-queue creates a fresh row.
                status: 'skipped',
                intakeCompletedAt: row.intakeCompletedAt || row.completedAt || null,
                reconciliationAt: reconciledAt,
                currentStage: null,
                errorHistory,
            });
            if (intakeShaped) reconciled += 1;
            else retired += 1;
        }
        return { healed: reconciled, retired };
    });
}

/**
 * Explicit one-action queue run: deterministic pre-processing first, then
 * prepare every successfully pre-processed project through Stage 1→5.
 *
 * Nothing calls this automatically when a project is queued. The UI must call
 * it from an operator-confirmed "Write all quotes" action. Failed intake jobs
 * stay failed (with their retained error history) and are never promoted to AI.
 */
export async function startAllQueuedProjects({
    preProcessingRunner,
    prepareQuoteRunner,
    onProgress,
    skipOnFailure = true,
    approvedJobIds = null,
} = {}) {
    if (_isActive || _isAllQueueActive) {
        throw new Error('startAllQueuedProjects: a queue is already active');
    }
    const approved = Array.isArray(approvedJobIds)
        ? new Set(approvedJobIds.filter(id => id !== null && id !== undefined))
        : null;
    const allRunId = `prepare-all:${_newBatchId()}`;
    _isAllQueueActive = true;
    _activeAllRunId = allRunId;
    _activeAllCohortJobIds = approved ? new Set(approved) : new Set();
    _leftRunJobIds = new Set();
    _abortRequested = false;
    _stopAfterCurrent = false;
    _stopKeepsNotStarted = false;
    _runEndedKeepingNotStarted = false;
    _allRunReadingDone = false;
    _activeAllRunCompletion = new Promise(resolve => {
        _resolveActiveAllRunCompletion = resolve;
    });
    const allRunAborted = () => (
        _abortRequested
        || preProcessingRunner?.abortSignal?.aborted === true
        || prepareQuoteRunner?.abortSignal?.aborted === true
    );

    try {

    // Make interrupted legacy residue actionable before calculating this run's
    // cohort. Invalid completed rows become failed/retryable with their prior
    // completion timestamp retained, rather than remaining a dead Ready row.
    await reconcileUnpromotableCompletedIntakes(db);

    const isApproved = row => !approved || approved.has(row.id);
    const cohortPreJobIds = new Set();
    const cohortPrepareJobIds = new Set();
    const cohortCompletedIntakeIds = new Set();
    const loadCohort = async ids => (await Promise.all(
        Array.from(ids).filter(id => !_leftRunJobIds.has(id)).map(id => db.batch_jobs.get(id)),
    )).filter(Boolean);
    const summarise = rows => ({
        completed: rows.filter(row => row.status === 'completed').length,
        failed: rows.filter(row => row.status === 'failed').length,
        pending: rows.filter(row => row.status === 'pending').length,
    });
    const summariseCurrentCohort = async () => {
        const [preRows, prepareRows] = await Promise.all([
            loadCohort(new Set([
                ...cohortPreJobIds,
                ...cohortCompletedIntakeIds,
            ])),
            loadCohort(cohortPrepareJobIds),
        ]);
        return {
            preProcessing: summarise(preRows),
            prepareQuote: summarise(prepareRows),
        };
    };
    const rowsAtStart = await listJobs();
    const prePending = rowsAtStart.filter(row => (
        row.status === 'pending'
        && (row.phase || 'prepare-quote') === 'pre-processing'
        && isApproved(row)
    ));
    prePending.forEach(row => cohortPreJobIds.add(row.id));
    const preparePendingAtStart = rowsAtStart.filter(row => (
        row.status === 'pending'
        && (row.phase || 'prepare-quote') === 'prepare-quote'
        && isApproved(row)
    ));
    preparePendingAtStart.forEach(row => cohortPrepareJobIds.add(row.id));
    const promotableAtStart = await findPromotableCompletedIntakes(rowsAtStart, db);
    promotableAtStart.filter(isApproved).forEach(row => cohortCompletedIntakeIds.add(row.id));
    _activeAllCohortJobIds = new Set([
        ...cohortPreJobIds,
        ...cohortPrepareJobIds,
        ...cohortCompletedIntakeIds,
    ]);
    if (allRunAborted()) {
        await _cancelPendingJobIds(_activeAllCohortJobIds, { batchId: allRunId });
        return summariseCurrentCohort();
    }

    if (prePending.length > 0) {
        if (!preProcessingRunner) {
            throw new Error('startAllQueuedProjects: preProcessingRunner is required while pre-processing jobs are pending');
        }
        await startPreProcessingQueue({
            pipelineRunner: preProcessingRunner,
            onProgress,
            skipOnFailure,
            guardUnsyncedFolders: true,
            jobIds: Array.from(cohortPreJobIds),
            allRunId,
        });
    }
    // This run reads no more files from here on (getQueueStatus readingDone).
    _allRunReadingDone = true;
    // Stop applies to the operator-confirmed run, not just the current phase.
    // Do not silently reset the abort flag by entering prepare-quote after the
    // preprocessing queue has crossed its cancellation boundary.
    if (allRunAborted()) {
        await _cancelPendingJobIds(_activeAllCohortJobIds, { batchId: allRunId });
        return summariseCurrentCohort();
    }

    // Promotion is a durable reconciliation boundary, not an in-memory tail
    // of the preprocessing loop. Scanning every completed intake row recovers
    // a browser/process crash that happened after completion but before the
    // prepare row was inserted. Any existing prepare row (including failed)
    // is respected so retries remain explicit and history is never duplicated.
    const [completedPreHistory, allPrepareHistory] = await Promise.all([
        listJobs({ status: ['completed'], phase: 'pre-processing' }),
        listJobs({ phase: 'prepare-quote' }),
    ]);
    // Deleted-project history is deliberately retained across Clear actions.
    // It is an enqueue integrity gate, never a crash-recovery candidate or
    // downstream phase lineage for a live project.
    const completedPreRows = completedPreHistory.filter(row => !isProjectDeletionTombstone(row));
    const allPrepareRows = allPrepareHistory.filter(row => !isProjectDeletionTombstone(row));
    const projectsWithPrepareHistory = new Set(allPrepareRows.map(row => row.projectId));
    for (const completed of completedPreRows) {
        if (allRunAborted()) {
            await _cancelPendingJobIds(_activeAllCohortJobIds, { batchId: allRunId });
            return summariseCurrentCohort();
        }
        if (projectsWithPrepareHistory.has(completed.projectId)) continue;
        if (!cohortPreJobIds.has(completed.id)
            && !cohortCompletedIntakeIds.has(completed.id)) continue;
        const prepared = _prepareEnqueue(completed.projectId, {
            phase: 'prepare-quote',
            selectedFileIds: completed.selectedFileIds || null,
            displayMeta: completed.displayMeta || null,
            restartLineage: _restartLineageFromJob(completed),
        });
        // Removal and promotion share the same cross-tab write transaction.
        // A completed intake read before the operator removed it must not
        // silently create a new runnable sibling afterwards.
        const promotion = await _withEnqueueWriteLock(async () => {
            const current = await db.batch_jobs.get(completed.id);
            if (!current || current.projectId !== completed.projectId
                || current.phase !== 'pre-processing'
                || current.status !== 'completed' || current.dismissedAt
                || isProjectDeletionTombstone(current)) return null;
            return _enqueuePrepared(prepared);
        });
        if (!promotion) continue;
        _emitCommittedEnqueue(promotion, prepared);
        const promoted = promotion.job;
        cohortPreJobIds.add(completed.id);
        cohortPrepareJobIds.add(promoted.id);
        _activeAllCohortJobIds.add(completed.id);
        _activeAllCohortJobIds.add(promoted.id);
        projectsWithPrepareHistory.add(completed.projectId);
        if (allRunAborted()) {
            await _cancelPendingJobIds(_activeAllCohortJobIds, { batchId: allRunId });
            return summariseCurrentCohort();
        }
    }

    if (allRunAborted()) {
        await _cancelPendingJobIds(_activeAllCohortJobIds, { batchId: allRunId });
        return summariseCurrentCohort();
    }
    const preparePending = (await listJobs({ status: ['pending'], phase: 'prepare-quote' }))
        .filter(row => cohortPrepareJobIds.has(row.id));
    if (preparePending.length > 0) {
        if (!prepareQuoteRunner) {
            throw new Error('startAllQueuedProjects: prepareQuoteRunner is required while prepare-quote jobs are pending');
        }
        await startPrepareQuoteQueue({
            pipelineRunner: prepareQuoteRunner,
            onProgress,
            skipOnFailure,
            guardUnsyncedFolders: true,
            jobIds: Array.from(cohortPrepareJobIds),
            allRunId,
        });
    }

    return summariseCurrentCohort();
    } finally {
        _isAllQueueActive = false;
        _activeAllRunId = null;
        _activeAllCohortJobIds = new Set();
        _abortRequested = false;
        _stopKeepsNotStarted = false;
        _runEndedKeepingNotStarted = false;
        // The whole Quote Run is over and getQueueStatus() says idle. Its
        // drains came while it was still active, and the run may write no
        // batch_jobs row after this (a Stop during an AI-service wait), so
        // this is the one signal every surface re-reads the run's end on.
        if (!_isActive) _emit('qm-batch-queue-idle', { allRunId });
        const resolveAllRunCompletion = _resolveActiveAllRunCompletion;
        _resolveActiveAllRunCompletion = null;
        resolveAllRunCompletion?.();
    }
}

/** Active phase (or null when idle). Surfaced for UI grouping. */
export function getActivePhase() {
    return _activePhase;
}

function _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function _queueAbortRequested(pipelineRunner) {
    return _abortRequested || pipelineRunner?.abortSignal?.aborted === true;
}

function _isAbortError(error) {
    return error?.name === 'AbortError'
        || (typeof DOMException !== 'undefined'
            && error instanceof DOMException
            && error.name === 'AbortError');
}

function _throwIfQueueAborted(pipelineRunner, message = 'Queue aborted by operator') {
    if (_queueAbortRequested(pipelineRunner)) throw new _CancelledError(message);
}

// Internal sentinel errors for control flow
class _CancelledError extends Error {
    constructor(msg) { super(msg); this.name = '_CancelledError'; }
}
class _SkippedError extends Error {
    constructor(msg) { super(msg); this.name = '_SkippedError'; }
}
class _LeaseLostError extends Error {
    constructor(msg) {
        super(msg);
        this.name = '_LeaseLostError';
        this.code = 'QUEUE_LEASE_LOST';
        this.retryable = true;
    }
}

/**
 * Pause after the current job's current stage completes. Resumable via
 * resumeQueue(). The pause check is between jobs, not between stages —
 * an in-flight stage finishes before the pause takes effect (DEC-BQ-007
 * + Q6 default in PHASE-2-QUEUE-DESIGN.md §12).
 */
export async function pauseQueue() {
    if (!_isActive) return;
    _isPaused = true;
    _emit('qm-batch-queue-paused', {
        batchId: _activeBatchId,
        phase: _activePhase,
        atJobId: _currentJobId,
    });
}

/**
 * Resume a paused queue.
 */
export async function resumeQueue() {
    if (!_isPaused) return;
    _isPaused = false;
    _emit('qm-batch-queue-resumed', {
        batchId: _activeBatchId,
        phase: _activePhase,
        atJobId: _currentJobId,
    });
}

/**
 * End the run the way a Stop does (no further job, and no further phase of a
 * prepare-all run) without cancelling the jobs it had not started: a run-wide
 * fault armed it, so they stay pending for the next run.
 */
function _endRunKeepingNotStarted() {
    _stopKeepsNotStarted = false;
    _runEndedKeepingNotStarted = true;
    _abortRequested = true;
    _isPaused = false;
}

/**
 * The projects this run froze that it never started, one per run-list row:
 * the ones still pending, and, when a prepare-all run ended while it read
 * files, the ones whose files it read but never drafted (completed intakes
 * the next run promotes, shown waiting on the run list).
 */
async function _countKeptNotStarted() {
    const cohort = _isAllQueueActive ? _activeAllCohortJobIds : _activeCohortJobIds;
    const projectIds = new Set();
    try {
        const rows = [];
        for (const jobId of cohort) {
            if (_leftRunJobIds.has(jobId)) continue;
            const row = await db.batch_jobs.get(jobId);
            if (row) rows.push(row);
        }
        for (const row of rows) {
            if (row.status === 'pending') projectIds.add(row.projectId);
        }
        if (_isAllQueueActive && !_allRunReadingDone) {
            const promotable = await findPromotableCompletedIntakes(await db.batch_jobs.toArray(), db);
            const frozen = new Set(rows.map(row => row.id));
            for (const row of promotable) {
                if (frozen.has(row.id)) projectIds.add(row.projectId);
            }
        }
    } catch { /* the count only words the end message */ }
    return projectIds.size;
}

async function _cancelPendingJobIds(jobIds, { phase = null, batchId = null } = {}) {
    // A run a run-wide fault ended keeps its jobs not started (they wait for
    // the next run), whichever sweep reaches them as the run unwinds.
    if (_runEndedKeepingNotStarted) return;
    const cohortIds = new Set(jobIds || []);
    if (cohortIds.size === 0) return;
    await _withTransaction(db, [db.batch_jobs], async () => {
        for (const jobId of cohortIds) {
            const row = await db.batch_jobs.get(jobId);
            if (!row
                || row.status !== 'pending'
                || (phase && (row.phase || 'prepare-quote') !== phase)) continue;
            await db.batch_jobs.update(row.id, {
                status: 'cancelled',
                // Swept because the run was stopped before this job started.
                cancelReason: 'run-stopped-before-start',
                batchId: batchId || row.batchId || null,
                completedAt: _nowIso(),
                currentStage: null,
            });
        }
    });
}

/**
 * Cancel the current job plus the frozen pending cohort. A prepare-all run
 * spans both phases, so its confirmed pending rows are cancelled together;
 * projects queued after confirmation remain pending for a future run.
 */
export async function abortQueue() {
    if (!_isActive && !_isAllQueueActive) return;
    _abortRequested = true;
    _isPaused = false; // force the pause loop to exit
    // Cancel only the frozen cohort approved for this run. Projects queued
    // after confirmation belong to a future run and must remain pending.
    if (_isAllQueueActive) {
        await _cancelPendingJobIds(_activeAllCohortJobIds, {
            batchId: _activeAllRunId,
        });
    } else if (_activeBatchId) {
        const phase = _activePhase || 'prepare-quote';
        await _cancelPendingJobIds(_activeCohortJobIds, {
            phase,
            batchId: _activeBatchId,
        });
    }
}

/**
 * Abort this tab's durable batch queue and wait until its active runner has
 * crossed every final Dexie mutation. The host must synchronously abort the
 * injected PipelineRunner signal before calling this boundary.
 */
export async function stopBatchQueueForSession() {
    const activeRunCompletion = _isAllQueueActive
        ? _activeAllRunCompletion
        : _activeRunCompletion;
    await abortQueue();
    await activeRunCompletion;
}

/**
 * Request that the currently-processing job be marked skipped at the next
 * awaited runner boundary. The request is bound to that durable job ID, so
 * it cannot leak to the next project. An already-running stage is not forcibly
 * interrupted; the processing loop records the terminal transition.
 */
let _skipRequestedJobId = null;

function _consumeSkipRequest(jobId) {
    if (_skipRequestedJobId !== jobId) return false;
    _skipRequestedJobId = null;
    return true;
}

function _throwIfSkipRequested(jobId) {
    if (_consumeSkipRequest(jobId)) {
        throw new _SkippedError('Queue job skipped by operator');
    }
}

export async function skipCurrent() {
    if (!_isActive || !_currentJobId) return;
    _skipRequestedJobId = _currentJobId;
    // Bind the request to the current durable row. The processing loop checks
    // it after every awaited stage/terminal boundary, so a late Skip cannot be
    // consumed by the next project.
    // We don't throw _SkippedError here directly — that has to come from
    // inside the processing loop at its next awaited boundary.
}

/**
 * Quote Run "Stop after this job". The job in progress finishes and keeps
 * its own result (a paid AI call is never thrown away); the run then ends
 * before claiming another job, and the jobs it had not started are cancelled
 * as not run (`run-stopped-before-start`), ready to be added back.
 *
 * With no job in progress (a wait for the AI service, the step between the
 * reading and drafting phases, a pause) there is nothing to finish, so the
 * run ends at once. Does nothing when no run is active. The flag never
 * carries into the next run: every run entry clears it.
 *
 * `keepNotStarted` (a run-wide fault, quote-run-controls onJobFailed): the run
 * ends the same way, but the jobs it had not started stay pending on the run
 * list for the next run, since each would have stopped on the same fault.
 */
export async function requestStopAfterCurrentJob({ keepNotStarted = false } = {}) {
    if (!_isActive && !_isAllQueueActive) return;
    if (_currentJobId === null || _currentJobId === undefined) {
        if (keepNotStarted) {
            _endRunKeepingNotStarted();
            return;
        }
        return abortQueue();
    }
    if (keepNotStarted) _stopKeepsNotStarted = true;
    if (_stopAfterCurrent) return;
    _stopAfterCurrent = true;
    _emit('qm-batch-queue-stop-after-current', {
        armed: true,
        batchId: _activeBatchId || _activeAllRunId,
        phase: _activePhase,
        atJobId: _currentJobId,
    });
}

/**
 * Quote Run "Keep going": undo Stop after this job while the job it was armed
 * during is still in progress, so the run carries on as if it was never
 * pressed.
 */
export function cancelStopAfterCurrentJob() {
    if (!_stopAfterCurrent) return;
    _stopAfterCurrent = false;
    _stopKeepsNotStarted = false;
    _emit('qm-batch-queue-stop-after-current', {
        armed: false,
        batchId: _activeBatchId || _activeAllRunId,
        phase: _activePhase,
        atJobId: _currentJobId,
    });
}

/**
 * Re-enqueue a failed job. Status flips back to 'pending', retryCount
 * increments, lastError preserved (for audit). Position becomes the next
 * pending position (last in line).
 *
 * @param {number} jobId
 * @param {{ selectedFileIds?: string[] | null, displayMeta?: object | null, projectRefresh?: object | null, db?: object }} [opts]
 */
export async function retryFailed(jobId, opts = {}) {
    const targetDb = opts?.db || db;
    if (!targetDb?.batch_jobs) throw new Error('retryFailed: batch_jobs store is unavailable');
    const projectRefresh = opts?.projectRefresh && typeof opts.projectRefresh === 'object'
        ? opts.projectRefresh
        : null;
    if (projectRefresh && !targetDb?.projects) {
        throw new Error('retryFailed: projects store is required for an atomic project refresh');
    }
    const selectedFileIdsProvided = Object.prototype.hasOwnProperty.call(opts || {}, 'selectedFileIds');
    const displayMetaProvided = Object.prototype.hasOwnProperty.call(opts || {}, 'displayMeta');
    const selectedFileIds = selectedFileIdsProvided
        ? _normaliseSelectedFileIds(opts.selectedFileIds, 'retryFailed')
        : undefined;
    const transactionTables = [
        targetDb.batch_jobs,
        ...(targetDb.projects ? [targetDb.projects] : []),
    ];
    const result = await _withTransaction(targetDb, transactionTables, async () => {
        const job = await targetDb.batch_jobs.get(jobId);
        if (!job) throw new Error(`retryFailed: job ${jobId} not found`);
        if (job.dismissedAt) {
            throw Object.assign(new Error('This job was removed from the run list. Add the project again from Import Projects to run it.'), {
                code: 'QUEUE_JOB_DISMISSED', retryable: false,
            });
        }
        if (job.status !== 'failed') {
            throw new Error(`retryFailed: job ${jobId} status is '${job.status}', only 'failed' can be retried`);
        }
        const phase = job.phase || 'prepare-quote';
        // Only a permission failure can safely reuse the exact deferred
        // descriptor. Incomplete reads/builds and changed sources must be
        // refreshed atomically from the current Import Projects scan.
        const canRetryDeferredSource = !!job.deferredIntake
            && job.lastErrorCode === 'SOURCE_PERMISSION_REQUIRED';
        const materializedProject = phase === 'pre-processing'
            && job.materializationState === 'complete'
            && targetDb.projects
            ? await targetDb.projects.get(job.projectId)
            : null;
        if (!projectRefresh && materializedProject) {
            if (!job.materializedAt
                || materializedProject.intakeMaterializedAt !== job.materializedAt) {
                const error = new Error(
                    'retryFailed: the materialised intake no longer matches this failed queue attempt',
                );
                error.code = 'PROJECT_RETRY_MATERIALIZATION_STALE';
                error.retryable = true;
                error.operatorAction = 'Open Import Projects, rescan the source folder, then retry from the current intake.';
                throw error;
            }
            if (projectHasGeneratedWork(materializedProject)) {
                const error = new Error(
                    `retryFailed: project ${job.projectId} already contains generated quote work`,
                );
                error.code = 'PROJECT_RETRY_REFRESH_ADVANCED';
                error.retryable = false;
                error.operatorAction = 'Open the existing project and continue from its saved stage. QuoteMaker did not restart preprocessing.';
                throw error;
            }
        }
        const canRetryMaterializedProject = !!materializedProject;
        if (phase === 'pre-processing'
            && !projectRefresh
            && !canRetryDeferredSource
            && !canRetryMaterializedProject) {
            const error = new Error(
                'retryFailed: intake retries must re-read the current source folder files from Import Projects first',
            );
            error.code = 'INTAKE_RETRY_REQUIRES_SOURCE_REFRESH';
            error.retryable = true;
            error.operatorAction = 'Open Menu → Import Projects, rescan the project folder, then use Retry project there.';
            throw error;
        }
        if (canRetryDeferredSource) {
            const sourceKey = String(job.deferredIntake?.sourceKey || '');
            const competingReservation = sourceKey
                ? (await targetDb.batch_jobs.toArray()).find(candidate => (
                    candidate?.id !== job.id
                    && !candidate?.dismissedAt
                    && candidate?.deferredIntake?.sourceKey === sourceKey
                    && (candidate.phase || 'prepare-quote') === 'pre-processing'
                    && ['pending', 'processing'].includes(candidate.status)
                ))
                : null;
            if (competingReservation) {
                const error = new Error(
                    'retryFailed: another queued project already owns this source folder',
                );
                error.code = 'DEFERRED_SOURCE_RESERVED';
                error.retryable = false;
                error.operatorAction = 'Open the preparation queue and continue with the existing folder reservation.';
                throw error;
            }
        }
        const nextPos = await _nextPendingPosition(phase, targetDb);
        if (projectRefresh) {
            const refreshProjectId = String(projectRefresh.projectId || '');
            if (!refreshProjectId || refreshProjectId !== String(job.projectId || '')) {
                const error = new Error('retryFailed: project refresh identity does not match the failed queue job');
                error.code = 'PROJECT_RETRY_REFRESH_IDENTITY_MISMATCH';
                error.retryable = false;
                throw error;
            }
            const currentProject = await targetDb.projects.get(job.projectId);
            if (!currentProject) {
                const createRow = projectRefresh.createRow && typeof projectRefresh.createRow === 'object'
                    ? { ...projectRefresh.createRow }
                    : null;
                if (!createRow) {
                    const error = new Error(`retryFailed: project ${job.projectId} is no longer available locally`);
                    error.code = 'PROJECT_NOT_LOCAL';
                    error.retryable = false;
                    throw error;
                }
                if (phase !== 'pre-processing'
                    || String(createRow.id || '') !== String(job.projectId || '')) {
                    const error = new Error('retryFailed: recreated project identity does not match the failed pre-processing job');
                    error.code = 'PROJECT_RETRY_RECREATE_IDENTITY_MISMATCH';
                    error.retryable = false;
                    throw error;
                }
                // The folder files have already been fully materialised by the
                // File Explorer. Recreate the missing Stage 1 row in the same
                // transaction as failed -> pending so the runner can never
                // claim an orphaned job between those two writes.
                await targetDb.projects.put(createRow);
            } else if (projectRefresh.createRow) {
                const error = new Error(
                    `retryFailed: project ${job.projectId} reappeared while the source folder was being read; rescan before retrying`,
                );
                error.code = 'PROJECT_RETRY_REFRESH_STALE';
                error.retryable = true;
                throw error;
            } else {
                const expected = projectRefresh.expectedState || {};
                const actual = {
                    status: currentProject.status ?? null,
                    currentStage: currentProject.currentStage ?? currentProject.current_stage ?? null,
                    updatedAt: currentProject.updated_at ?? currentProject.updatedAt ?? null,
                };
                if (Object.keys(expected).some(key => String(expected[key] ?? '') !== String(actual[key] ?? ''))) {
                    const error = new Error(
                        `retryFailed: project ${job.projectId} changed while the source folder was being read; rescan before retrying`,
                    );
                    error.code = 'PROJECT_RETRY_REFRESH_STALE';
                    error.retryable = true;
                    throw error;
                }
                const projectPatch = projectRefresh.patch && typeof projectRefresh.patch === 'object'
                    ? { ...projectRefresh.patch }
                    : null;
                if (!projectPatch) {
                    throw new Error('retryFailed: project refresh patch is required');
                }
                delete projectPatch.id;
                await targetDb.projects.update(job.projectId, projectPatch);
            }
        }
        const patch = {
            status: 'pending',
            batchId: null,
            position: nextPos,
            startedAt: null,
            completedAt: null,
            currentStage: null,
            claimOwnerId: null,
            leaseHeartbeatAt: null,
            leaseExpiresAt: null,
            retryCount: (job.retryCount || 0) + 1,
            // A deliberate "Try again" gets the full set of automatic provider
            // waits. Without this the count would carry (see
            // _providerRetriesThisRun) and the retry could start with none.
            providerRetryCount: 0,
            providerRetryBatchId: null,
            providerWaitUntil: null,
            // preserve lastError + lastErrorStage for audit
        };
        // The page-closed stamp (hydrate, recoverExpiredQueueLeases) belongs
        // to the attempt it reset. Try again keeps it only while it can still
        // matter: the closed page's server claim may hold the project, and
        // import-workflow closedPageClaimHold reads the stamp to say so when
        // the next claim is refused as busy. Only a drafting job claims the
        // project on the server, so only a drafting job can keep it, and only
        // while no attempt since the reset began a stage of its own
        // (stageStartedAt, written as each stage begins) or took a claim of
        // its own (quoteRunClaimedAt, written as a claim is granted). An
        // attempt that did either stopped for its own reason, and its claim
        // replaced the closed page's claim on the row, so the stamp is stale
        // whatever the error code. The dead run's own stages and claims came
        // at least a lease before the reset, so one at the reset's
        // millisecond is newer.
        const stampedMs = Date.parse(String(job.interruptedAt || ''));
        const sinceReset = (iso) => Date.parse(String(iso || '')) >= stampedMs;
        const keepStamp = Number.isFinite(stampedMs)
            && phase === 'prepare-quote'
            && !sinceReset(job.stageStartedAt)
            && !sinceReset(job.quoteRunClaimedAt);
        if (!keepStamp) {
            patch.interruptedAt = null;
            patch.interruptedAtStage = null;
            patch.interruptedOwnerId = null;
        }
        if (job.deferredIntake) {
            patch.materializationState = 'queued';
            patch.materializationStartedAt = null;
        }
        if (selectedFileIdsProvided) patch.selectedFileIds = selectedFileIds;
        if (displayMetaProvided) {
            patch.displayMeta = opts.displayMeta && typeof opts.displayMeta === 'object'
                ? _mergeDisplayMetaNonBlank(job.displayMeta, opts.displayMeta, job.projectId)
                : job.displayMeta || null;
        }
        await targetDb.batch_jobs.update(jobId, patch);
        return {
            previous: job,
            current: await targetDb.batch_jobs.get(jobId),
            phase,
            nextPos,
        };
    });
    // Try again and Restore folder access keep the job's id. Put back while a
    // run goes, it waits for the next run like any job put back (queueStaged):
    // this run does not claim it and its Stop does not cancel it.
    _leaveActiveRun(jobId);
    const job = result.previous;
    const retriedJob = result.current;
    _emit('qm-batch-queue-enqueued', {
        jobId,
        projectId: job.projectId,
        displayMeta: retriedJob?.displayMeta || null,
        selectedFileIds: retriedJob?.selectedFileIds || null,
        phase: result.phase,
        position: result.nextPos,
        batchId: null,
        retried: true,
        retainedError: job.lastError ? {
            message: job.lastError,
            code: job.lastErrorCode || null,
            retryable: job.lastErrorRetryable !== false,
            details: job.lastErrorDetails || null,
        } : null,
    });
    return retriedJob;
}

/**
 * Permanently dismiss a failed job from the DLQ. Symmetric with retryFailed
 * — only failed jobs can be dismissed. The row is deleted from Dexie. Use
 * when the operator has decided not to retry (e.g. genuinely empty project,
 * or already manually completed via Stage 1 outside the queue).
 *
 * @param {number} jobId
 */
/**
 * Record that the operator has looked at a stopped project, so the row can
 * offer the retry its own copy promises.
 *
 * A Quote Run coordination stop ("Run paused for review") deliberately sends
 * the operator to the project before spending on a retry, because another
 * session may have moved it on. But the review was a dead end: the row's only
 * action was to open the project again, so "Review the latest project, then
 * try again" named a "try again" that did not exist anywhere. Waverton sat in
 * that state after the 2026-09-20 run with no way back onto the run list.
 *
 * The acknowledgement is pinned to the failure it was given for (`completedAt`
 * is the terminal stamp), so a LATER stop puts the row back behind the gate
 * rather than inheriting an old operator's glance.
 */
export async function acknowledgeCoordinationReview(jobId, opts = {}) {
    const targetDb = opts?.db || db;
    if (!targetDb?.batch_jobs) throw new Error('acknowledgeCoordinationReview: batch_jobs store is unavailable');
    const job = await targetDb.batch_jobs.get(jobId);
    if (!job) return { acknowledged: false, reason: 'not-found' };
    if (job.status !== 'failed') return { acknowledged: false, reason: 'not-failed' };
    const reviewedFor = job.completedAt || job.updatedAt || null;
    await targetDb.batch_jobs.update(jobId, {
        coordinationReviewedAt: new Date().toISOString(),
        coordinationReviewedFor: reviewedFor,
    });
    return { acknowledged: true, reviewedFor };
}

export async function dismissFailed(jobId) {
    const job = await db.batch_jobs.get(jobId);
    if (!job) throw new Error(`dismissFailed: job ${jobId} not found`);
    if (isProjectDeletionTombstone(job)) {
        const error = new Error(
            `dismissFailed: job ${jobId} is permanent project-deletion audit history and cannot be dismissed`,
        );
        error.code = 'PROJECT_DELETED_TOMBSTONE';
        error.retryable = false;
        throw error;
    }
    if (job.status !== 'failed') {
        throw new Error(`dismissFailed: job ${jobId} status is '${job.status}', only 'failed' can be dismissed`);
    }
    await db.batch_jobs.delete(jobId);
    _emit('qm-batch-queue-dlq-dismissed', {
        jobId,
        projectId: job.projectId,
        batchId: job.batchId,
    });
}

/** Remove one stopped project's card, retaining its files and queue history. */
export async function dismissStoppedProject(projectId, opts = {}) {
    const targetDb = opts.db || db;
    if (!projectId || !targetDb?.batch_jobs) return { removed: false, reason: 'bad-args' };
    const result = await _withEnqueueWriteLock(async () => {
        const history = await targetDb.batch_jobs.where('projectId').equals(projectId).toArray();
        if (history.some(isProjectDeletionTombstone)) {
            throw Object.assign(new Error('Deleted-project audit history cannot be removed.'), {
                code: 'PROJECT_DELETED_TOMBSTONE', retryable: false,
            });
        }
        const rows = history.filter(row => !row.dismissedAt);
        if (!rows.length) return { removed: false, reason: 'already-removed' };
        if (rows.some(row => !['failed', 'completed', 'cancelled', 'skipped'].includes(row.status))) {
            throw Object.assign(new Error('This project is still staged, queued or running. Stop or take it off the run before removing it.'), {
                code: 'QUEUE_PROJECT_ACTIVE', retryable: true,
            });
        }
        const dismissedAt = _nowIso();
        for (const row of rows) await targetDb.batch_jobs.update(row.id, { dismissedAt });
        return { removed: true, jobIds: rows.map(row => row.id) };
    }, targetDb);
    if (result.removed) {
        _emit('qm-batch-queue-dlq-dismissed', { projectId, jobId: result.jobIds[0], jobIds: result.jobIds });
        _emit('qm-batch-queue-card-cleared', { projectId });
    }
    return result;
}

/**
 * Retire every re-runnable queue row before a project is deleted.
 *
 * A processing row is a hard blocker: deleting while its pipeline still owns
 * the project would allow the trailing save to recreate it. Pending, failed,
 * skipped, staged and waiting-files rows are atomically converted to cancelled
 * tombstones so neither queue startup, Retry, nor queueStaged can resurrect
 * the deleted UUID later.
 */
export async function retireProjectJobs(projectId, {
    reason = 'project-deleted',
    db: targetDb = db,
} = {}) {
    if (!projectId || typeof projectId !== 'string') {
        throw new Error('retireProjectJobs: projectId is required (string)');
    }
    if (!targetDb?.batch_jobs) {
        throw new Error('retireProjectJobs: batch_jobs store is unavailable');
    }
    const retiredAt = _nowIso();
    const operation = async () => {
        const rows = await targetDb.batch_jobs.where('projectId').equals(projectId).toArray();
        const processing = rows.find(job => job.status === 'processing');
        if (processing) {
            const error = new Error('This project is currently being processed. Stop or finish its queue job before deleting it.');
            error.code = 'PROJECT_DELETE_QUEUE_ACTIVE';
            error.retryable = true;
            error.details = { jobId: processing.id, phase: processing.phase || 'prepare-quote' };
            throw error;
        }
        const rerunnable = rows.filter(job => (
            job.status === 'pending'
            || job.status === 'failed'
            || job.status === 'skipped'
            // Staged/waiting-files rows are re-runnable BY DESIGN (queueStaged
            // flips them to pending), so they retire to cancelled tombstones
            // like every other resurrectable row — a deleted project must
            // never re-enter a runnable status under its retired UUID.
            || job.status === 'staged'
            || job.status === 'waiting-files'
        ));
        const rerunnableIds = new Set(rerunnable.map(job => job.id));
        for (const job of rows) {
            const deletionMarker = {
                projectRetiredAt: retiredAt,
                projectRetiredReason: reason,
            };
            await targetDb.batch_jobs.update(job.id, rerunnableIds.has(job.id)
                ? {
                    ...deletionMarker,
                    status: 'cancelled',
                    completedAt: retiredAt,
                    completionOutcome: reason,
                    reviewCode: 'PROJECT_DELETED',
                    reviewMessage: 'Queue work retired before project deletion.',
                    reviewDetails: { reason, retiredAt },
                    claimOwnerId: null,
                    leaseHeartbeatAt: null,
                    leaseExpiresAt: null,
                    stalledSince: null,
                }
                : deletionMarker);
        }
        return rerunnable.map(job => job.id);
    };
    const retiredJobIds = typeof targetDb.transaction === 'function'
        ? await targetDb.transaction('rw', targetDb.batch_jobs, operation)
        : await operation();
    for (const jobId of retiredJobIds) {
        _emit('qm-batch-job-cancelled', { jobId, projectId, reason, retiredAt });
    }
    return { projectId, retiredJobIds, retiredAt };
}

// ─── PUBLIC API: persistence ───────────────────────────────────────────────

/**
 * Read Dexie batch_jobs into memory on app boot. Marks any rows still in
 * 'processing' status as 'pending' (the prior session crashed/closed mid-
 * stage; safest to re-run).
 *
 * `healedIntakes` counts proofless completed intake rows returned to pending
 * by this call and `retiredIntakes` counts those retired to skipped audit
 * history, so the mount surface can tell the operator what changed rather than
 * silently altering a row's meaning. `interrupted` lists the expired leases
 * this call reset: the job, its project and the stage the dead run reached.
 *
 * @returns {Promise<{ hasRecoverableQueue: boolean, lastBatch: { batchId: string|null, pending: number, promotableReady?: number, completed: number, failed: number } | null, healedIntakes?: number, retiredIntakes?: number, interrupted?: Array<{ jobId: number, projectId: string, stage: number|string|null }> }>}
 */
export async function hydrate() {
    if (!db?.batch_jobs) {
        return { hasRecoverableQueue: false, lastBatch: null, healedIntakes: 0, retiredIntakes: 0, interrupted: [] };
    }
    // Recover only expired (or legacy lease-less) processing rows. A different
    // live tab may legitimately own a processing row; resetting it on every
    // drawer open allowed duplicate AI work across tabs.
    const interrupted = await _withTransaction(db, [db.batch_jobs], async () => {
        const processing = await db.batch_jobs.where('status').equals('processing').toArray();
        const nowMs = Date.now();
        const reset = [];
        for (const row of processing) {
            if (!_leaseIsExpired(row, nowMs)) continue;
            const stage = row.currentStage ?? null;
            await db.batch_jobs.update(row.id, {
                status: 'pending',
                batchId: null,
                currentStage: null,
                claimOwnerId: null,
                leaseHeartbeatAt: null,
                leaseExpiresAt: null,
                // When the run holding it died, and the stage it had reached.
                interruptedAt: _nowIso(),
                interruptedAtStage: stage,
                // The runner that held it (see _recoverExpiredProcessingLeases).
                interruptedOwnerId: row.claimOwnerId ?? null,
            });
            reset.push({ jobId: row.id, projectId: row.projectId, stage });
        }
        return reset;
    }) || [];

    // A completed intake without exact durable readiness proof cannot be
    // promoted. Reconcile it back to a pending intake row (self-heal,
    // 2026-07-29) while preserving the original completion timestamp in the
    // audit trail — the resume banner then offers to run it rather than
    // presenting a permanent inert Ready row. The healed count is returned so
    // the mount surface can TELL the operator work was re-queued.
    const { healed: healedIntakes, retired: retiredIntakes } = await reconcileUnpromotableCompletedIntakes(db);

    // Find the most-recent batchId with any pending or recently-completed
    // rows. If found, return its summary so the UI can offer Resume / Clear.
    const allRows = (await db.batch_jobs.toArray()).filter(row => !row.dismissedAt);
    if (allRows.length === 0) {
        return { hasRecoverableQueue: false, lastBatch: null, healedIntakes, retiredIntakes, interrupted };
    }
    const promotableReady = await findPromotableCompletedIntakes(allRows, db);
    // Group by batchId; pick the batch whose most-recent enqueue is newest
    const batches = new Map();
    for (const row of allRows) {
        if (!row.batchId) continue;
        if (!batches.has(row.batchId)) {
            batches.set(row.batchId, { batchId: row.batchId, latestEnqueue: row.enqueuedAt, rows: [] });
        }
        const bucket = batches.get(row.batchId);
        bucket.rows.push(row);
        if (row.enqueuedAt > bucket.latestEnqueue) {
            bucket.latestEnqueue = row.enqueuedAt;
        }
    }
    if (batches.size === 0) {
        // All rows are batchId-null (never started). Treat as recoverable
        // pending bucket.
        const pending = allRows.filter(r => r.status === 'pending').length;
        const recoverable = pending > 0 || promotableReady.length > 0;
        return {
            hasRecoverableQueue: recoverable,
            lastBatch: recoverable ? {
                batchId: null,
                pending,
                promotableReady: promotableReady.length,
                completed: 0,
                failed: 0,
            } : null,
            healedIntakes,
            retiredIntakes,
            interrupted,
        };
    }
    const sortedBatches = Array.from(batches.values())
        .sort((a, b) => (b.latestEnqueue || '').localeCompare(a.latestEnqueue || ''));
    const newest = sortedBatches[0];
    // DEC-TQ-001: per-phase pending counts so the resume-banner can decide
    // which phase to offer to resume.
    // startQueue claims every pending row for the phase regardless of its old
    // batch id. Report that same recoverable set here: expired processing rows
    // are deliberately reset to batchId=null, and must not disappear merely
    // because a completed/failed historical batch also exists.
    const pendingRows = allRows.filter(row => row.status === 'pending');
    const summary = {
        batchId: newest.batchId,
        pending: pendingRows.length,
        pendingPreProcessing: pendingRows.filter(r => (r.phase || 'prepare-quote') === 'pre-processing').length,
        pendingPrepareQuote: pendingRows.filter(r => (r.phase || 'prepare-quote') === 'prepare-quote').length,
        completed: newest.rows.filter(r => r.status === 'completed').length,
        failed: newest.rows.filter(r => r.status === 'failed').length,
        promotableReady: promotableReady.length,
    };
    return {
        hasRecoverableQueue: summary.pending > 0 || summary.promotableReady > 0,
        lastBatch: summary,
        healedIntakes,
        retiredIntakes,
        interrupted,
    };
}

/**
 * Wipe ordinary batch_jobs rows. With no opts, clears every operator-facing
 * queue row. With opts.phase, clears only that phase's ordinary rows
 * (DEC-TQ-001 — "Clear pre-processing" / "Clear prepare-quote"). Permanent
 * project-deletion tombstones are audit history and survive every clear mode;
 * enqueue() needs them to keep retired project UUIDs retired after a rescan.
 *
 * STAGED and WAITING-FILES rows also survive: they are the operator's parked,
 * hand-curated selections — NOT run history. "Clear queue" means the queue;
 * a staged card is removed from its own tab (removeStaged), never as a side
 * effect of clearing finished runs.
 *
 * @param {{ phase?: 'pre-processing' | 'prepare-quote', db?: object }} [opts]
 */
export async function clearAll(opts = {}) {
    const targetDb = opts?.db || db;
    if (!targetDb?.batch_jobs) return;
    const phase = opts && typeof opts === 'object' ? opts.phase : undefined;
    if (phase && phase !== 'pre-processing' && phase !== 'prepare-quote') {
        throw new Error(`clearAll: phase must be 'pre-processing' or 'prepare-quote' (or omitted for all), got '${phase}'`);
    }
    await _withTransaction(targetDb, [targetDb.batch_jobs], async () => {
        const all = await targetDb.batch_jobs.toArray();
        const liveLeases = all.filter(row => (
            row.status === 'processing'
            && (!phase || (row.phase || 'prepare-quote') === phase)
            && !_leaseIsExpired(row)
        ));
        if (liveLeases.length > 0) {
            const error = new Error(
                `clearAll: cannot clear the queue while ${liveLeases.length} project${liveLeases.length === 1 ? '' : 's'} ` +
                'is actively leased by a browser tab. Stop that preparation and wait for it to finish first.',
            );
            error.code = 'QUEUE_LIVE_LEASE_CONFLICT';
            error.retryable = true;
            error.jobIds = liveLeases.map(row => row.id);
            throw error;
        }
        const rows = all.filter(row => (
            (!phase || (row.phase || 'prepare-quote') === phase)
            && !isProjectDeletionTombstone(row)
            && !_isStagedStatus(row.status)
        ));
        for (const row of rows) await targetDb.batch_jobs.delete(row.id);
    });
    if (!phase) {
        _isActive = false;
        _isPaused = false;
        _abortRequested = false;
        _activeBatchId = null;
        _activePhase = null;
        _currentJobId = null;
        _activeCohortJobIds = new Set();
        _isAllQueueActive = false;
        _activeAllRunId = null;
        _activeAllCohortJobIds = new Set();
        _resolveActiveAllRunCompletion?.();
        _resolveActiveAllRunCompletion = null;
        _activeAllRunCompletion = Promise.resolve();
        _skipRequestedJobId = null;
        _emit('qm-batch-queue-cleared', { phase: 'all' });
        return;
    }
    _emit('qm-batch-queue-cleared', { phase });
}

// ─── Test-only: reset module state (NOT exported in production builds) ─────
// Tests need to reset module-scope flags between runs. Production code does
// not call this. The function is exported but has no production caller.
export function _resetModuleStateForTests() {
    _resolveActiveRunCompletion?.();
    _isActive = false;
    _isPaused = false;
    _abortRequested = false;
    _stopAfterCurrent = false;
    _stopKeepsNotStarted = false;
    _runEndedKeepingNotStarted = false;
    _activeBatchId = null;
    _activePhase = null;
    _currentJobId = null;
    _activeCohortJobIds = new Set();
    _isAllQueueActive = false;
    _activeAllRunId = null;
    _activeAllCohortJobIds = new Set();
    _leftRunJobIds = new Set();
    _skipRequestedJobId = null;
    _enqueueSerial = Promise.resolve();
    _runnerOwnerId = null;
    _activeRunCompletion = Promise.resolve();
    _resolveActiveRunCompletion = null;
    _resolveActiveAllRunCompletion?.();
    _activeAllRunCompletion = Promise.resolve();
    _resolveActiveAllRunCompletion = null;
}

/**
 * Count pending/processing/completed/failed/skipped/cancelled jobs grouped
 * by phase. Powers the two-phase queue summary chips (pre-processing vs
 * prepare-quote).
 *
 * @returns {Promise<{ preProcessing: object, prepareQuote: object }>}
 */
export async function getCountsByPhase() {
    if (!db?.batch_jobs) {
        const empty = { pending: 0, processing: 0, completed: 0, failed: 0, skipped: 0, cancelled: 0 };
        return { preProcessing: { ...empty }, prepareQuote: { ...empty } };
    }
    const all = await db.batch_jobs.toArray();
    const tally = (phase) => {
        const counts = { pending: 0, processing: 0, completed: 0, failed: 0, skipped: 0, cancelled: 0 };
        for (const r of all) {
            if (r.dismissedAt) continue;
            if ((r.phase || 'prepare-quote') !== phase) continue;
            if (counts[r.status] != null) counts[r.status] += 1;
        }
        return counts;
    };
    return {
        preProcessing: tally('pre-processing'),
        prepareQuote: tally('prepare-quote'),
    };
}

// ─── Test-only surface ─────────────────────────────────────────────────────
// The stall watchdog's decision is exported so it can be unit-tested directly.
// Its wiring lives inside a setInterval created deep in startQueue's async
// body, which no fake-timer harness in this repo could be made to drive — so
// the decision is proven here rather than inferred from a timer that may never
// have fired. Not part of the module's public API; do not call from app code.
export const _stallWatchdogInternals = {
    shouldReportStall: _shouldReportStall,
    normaliseBatchError: _normaliseBatchError,
    JOB_STALL_WARN_MS,
};
